// Sunice Product Card JavaScript
// Handles custom variant selection and interactions for product cards

// CRITICAL: Expose functions immediately for onclick handlers (before IIFE)
// This ensures functions are available even if script loads with defer/module
window.toggleProductForm = function(button) {
  if (!(button instanceof HTMLElement)) return;
  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;
  
  // Basic implementation that works immediately
  if (window.innerWidth < 750) {
    // Mobile: try to open modal if function exists
    if (typeof window.openSuniceProductFormModal === 'function') {
      window.openSuniceProductFormModal(productCard);
    } else {
      // Fallback: show form inline
      productCard.classList.add('active');
      const selectButton = productCard.querySelector('.select-size-button');
      const closeButton = productCard.querySelector('.close-form-button');
      if (selectButton) selectButton.style.display = 'none';
      if (closeButton) closeButton.style.display = 'inline-block';
    }
    return;
  }
  
  // Desktop: show inline form
  productCard.classList.add('active');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');
  if (selectButton) selectButton.style.display = 'none';
  if (closeButton) closeButton.style.display = 'inline-block';
  // Ensure variant picker and Core legend are initialized (for paginated/dynamic cards)
  if (typeof window.initializeSuniceProductCardComponents === 'function') {
    setTimeout(function() { window.initializeSuniceProductCardComponents(); }, 0);
  }
};

window.closeProductForm = function(button) {
  if (!(button instanceof HTMLElement)) return;
  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;
  
  // Basic implementation that works immediately
  if (window.innerWidth < 750) {
    if (typeof window.closeSuniceProductFormModal === 'function') {
      window.closeSuniceProductFormModal();
    } else {
      const modalEl = document.querySelector('.sunice-product-form-modal');
      if (modalEl) {
        modalEl.classList.remove('is-open');
        document.documentElement.classList.remove('sunice-product-form-modal-open');
      }
    }
    return;
  }
  
  // Desktop: hide inline form
  productCard.classList.remove('active');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');
  if (selectButton) selectButton.style.display = 'inline-block';
  if (closeButton) closeButton.style.display = 'none';
};

// Stub until deferred script loads; replaced by full implementation below.
window.handleShopifyAddToCart = window.handleShopifyAddToCart || function(event, productId) {
  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
  if (typeof window.__suniceRunProductCardAddToCart === 'function') {
    window.__suniceRunProductCardAddToCart(event, productId);
  }
};

// Guard: avoid redeclaration in Theme Editor when assets are re-injected
(function() {
  'use strict';
  
  const globalWindow = /** @type {Record<string, any>} */ (window);

  // ============================================
  // PERFORMANCE OPTIMIZATIONS
  // ============================================
  
  // Memoized URL normalization function (caches results)
  const normalizeUrlCache = new Map();
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (normalizeUrlCache.has(url)) {
      return normalizeUrlCache.get(url);
    }
    const withoutHost = url.replace(/^https?:\/\/[^/]+/i, '');
    const normalized = withoutHost.split('?')[0].split('#')[0];
    normalizeUrlCache.set(url, normalized);
    return normalized;
  };
  
  // Cache parsed product images JSON per product card
  const productImagesCache = new WeakMap();
  const getProductImages = (productCard) => {
    if (!(productCard instanceof HTMLElement)) return null;
    if (productImagesCache.has(productCard)) {
      return productImagesCache.get(productCard);
    }
    try {
      const raw = productCard.dataset.productImages;
      if (raw) {
        const parsed = JSON.parse(raw);
        productImagesCache.set(productCard, parsed);
        return parsed;
      }
    } catch (e) {
      // ignore parse errors
    }
    return null;
  };
  
  // Debounce helper for expensive operations
  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(null, args), delay);
    };
  };
  
  // requestIdleCallback polyfill for older browsers
  const requestIdleCallback = window.requestIdleCallback || 
    ((cb) => setTimeout(cb, 1));
  
  // Throttle helper for scroll/resize events
  const throttle = (fn, delay) => {
    let lastCall = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        fn.apply(null, args);
      }
    };
  };

  /**
   * Delegated close handler:
   * Ensures `.sunice-variant-picker__close` always closes the variant popover (desktop)
   * or the full-screen modal (mobile), even for dynamically injected cards.
   */
  if (!globalWindow.__suniceVariantPickerCloseDelegated) {
    globalWindow.__suniceVariantPickerCloseDelegated = true;
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        // Check if click is on the close button or its child (SVG)
        const closeBtn = target.closest('.sunice-variant-picker__close');
        if (!(closeBtn instanceof HTMLElement)) return;

        // Prevent bubbling to any other click handlers (e.g., outside-click close logic)
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Mobile: close the modal if it exists
        if (window.innerWidth < 750) {
          try {
            /** @type {any} */
            const gw = window;
            if (typeof gw.closeSuniceProductFormModal === 'function') {
              gw.closeSuniceProductFormModal();
              return;
            }
          } catch (e) {
            // ignore
          }

          // Fallback: close modal by class toggles if global close function isn't available
          const modalEl = document.querySelector('.sunice-product-form-modal');
          if (modalEl instanceof HTMLElement) {
            modalEl.classList.remove('is-open');
            modalEl.removeAttribute('data-product-id');
            const contentEl = modalEl.querySelector('.sunice-product-form-modal__content');
            if (contentEl instanceof HTMLElement) contentEl.innerHTML = '';
            document.documentElement.classList.remove('sunice-product-form-modal-open');
          }
          return;
        }

        // Desktop: close the inline popover by removing `.active` on the card
        const productCard = closeBtn.closest('.sunice-product-card');
        if (!(productCard instanceof HTMLElement)) {
          // If no product card found, try to find the picker and close it
          const picker = closeBtn.closest('.sunice-variant-picker');
          if (picker instanceof HTMLElement) {
            const card = picker.closest('.sunice-product-card');
            if (card instanceof HTMLElement) {
              card.classList.remove('active');
              const selectButton = card.querySelector('.select-size-button');
              const closeButton = card.querySelector('.close-form-button');
              if (selectButton instanceof HTMLElement) selectButton.style.display = 'inline-block';
              if (closeButton instanceof HTMLElement) closeButton.style.display = 'none';
              card.removeAttribute('data-outside-click-handler');
            }
          }
          return;
        }

        // If the theme ever switches this container to a real Popover API element, close it too.
        const formContainer = productCard.querySelector('.product-form-container');
        if (formContainer instanceof HTMLElement) {
          /** @type {any} */
          const anyEl = formContainer;
          if (typeof anyEl.hidePopover === 'function') {
            try {
              anyEl.hidePopover();
            } catch (e) {
              // ignore
            }
          }
          if (anyEl instanceof HTMLDialogElement && anyEl.open) {
            try {
              anyEl.close();
            } catch (e) {
              // ignore
            }
          }
        }

        // Mirror closeProductForm() behavior without needing a temp button
        productCard.classList.remove('active');
        const selectButton = productCard.querySelector('.select-size-button');
        const closeButton = productCard.querySelector('.close-form-button');
        if (selectButton instanceof HTMLElement) selectButton.style.display = 'inline-block';
        if (closeButton instanceof HTMLElement) closeButton.style.display = 'none';

        // Remove the flag so outside-click handler can be reattached cleanly next open
        productCard.removeAttribute('data-outside-click-handler');
      },
      true // Use capture phase for faster response
    );
  }

// Make functions globally available for theme editor compatibility
/**
 * @param {HTMLElement} button
 */
function toggleProductForm(button) {
  if (!(button instanceof HTMLElement)) return;

  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  // On mobile (< 750px), open a full-screen modal instead of inline popover
  if (window.innerWidth < 750) {
    openMobileProductFormModal(productCard);
    return;
  }

  const formContainer = productCard.querySelector('.product-form-container');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');

  // Add active class to show form (desktop popover behavior)
  productCard.classList.add('active');

  // Show close button, hide select button
  if (selectButton instanceof HTMLElement) selectButton.style.display = 'none';
  if (closeButton instanceof HTMLElement) closeButton.style.display = 'inline-block';

  // Always sync variant ID + lower-tier options (e.g. inseam) when the popover opens.
  // Liquid may pre-check color only while the hidden variant id input stays empty.
  const picker = productCard.querySelector('.sunice-variant-picker');
  if (picker instanceof HTMLElement) {
    requestAnimationFrame(() => {
      setInitialVariantSelection(picker);
    });
  }
  
  // Add outside click handler to close picker when clicking outside the card
  // Use a small delay to avoid immediate closure from the button click that opened it
  setTimeout(() => {
    const handleOutsideClick = function(e) {
      // Don't close if clicking on the close button (it has its own handler)
      if (e.target.closest('.sunice-variant-picker__close')) {
        return;
      }
      
      // Check if the click is outside the product card
      if (!productCard.contains(e.target)) {
        // Only close if the picker is currently visible (product card has active class)
        if (productCard.classList.contains('active')) {
          if (typeof globalWindow.closeProductForm === 'function') {
            const tempButton = document.createElement('button');
            tempButton.classList.add('close-form-button');
            productCard.appendChild(tempButton);
            globalWindow.closeProductForm(tempButton);
            tempButton.remove();
          }
        }
        // Remove listener after closing
        document.removeEventListener('click', handleOutsideClick);
      }
    };
    
    // Add click listener to document (only if not already added)
    if (!productCard.dataset.outsideClickHandler) {
      productCard.dataset.outsideClickHandler = 'true';
      document.addEventListener('click', handleOutsideClick);
      
      // Remove the flag when the picker is closed
      const observer = new MutationObserver(function(mutations) {
        if (!productCard.classList.contains('active')) {
          document.removeEventListener('click', handleOutsideClick);
          productCard.removeAttribute('data-outside-click-handler');
          observer.disconnect();
        }
      });
      observer.observe(productCard, { attributes: true, attributeFilter: ['class'] });
    }
  }, 100);
}

// Expose functions to global window immediately for onclick handlers
// Replace the stub functions defined at the top of the file
globalWindow.toggleProductForm = toggleProductForm;
window.toggleProductForm = toggleProductForm;
globalWindow.closeProductForm = closeProductForm;
window.closeProductForm = closeProductForm;
// Expose modal function globally
globalWindow.openSuniceProductFormModal = openMobileProductFormModal;
window.openSuniceProductFormModal = openMobileProductFormModal;

/**
 * @param {HTMLElement} button
 */
function closeProductForm(button) {
  if (!(button instanceof HTMLElement)) return;

  const productCard = button.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  // On mobile, close the full-screen modal instead of toggling inline popover
  if (window.innerWidth < 750) {
    try {
      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.closeSuniceProductFormModal === 'function') {
        globalWindow.closeSuniceProductFormModal();
      }
    } catch (e) {
      // Failed to close product form modal from close button
    }
    return;
  }

  const formContainer = productCard.querySelector('.product-form-container');
  const selectButton = productCard.querySelector('.select-size-button');
  const closeButton = productCard.querySelector('.close-form-button');

  // Remove active class to hide form
  productCard.classList.remove('active');

  // Show select button, hide close button
  if (selectButton instanceof HTMLElement) selectButton.style.display = 'inline-block';
  if (closeButton instanceof HTMLElement) closeButton.style.display = 'none';
};

/**
 * Open product form in a modal on mobile (< 750px).
 * Reuses the same form markup/styles, but shows it in a full-screen bottom sheet.
 * @param {HTMLElement} productCard
 */
function openMobileProductFormModal(productCard) {
  // Only for small screens
  if (window.innerWidth >= 750) return;

  /** @type {any} */
  const globalWindow = window;

  let modalEl = document.querySelector('.sunice-product-form-modal');
  if (!(modalEl instanceof HTMLElement)) {
    modalEl = document.createElement('div');
    modalEl.className = 'sunice-product-form-modal';
    modalEl.innerHTML = `
      <div class="sunice-product-form-modal__backdrop"></div>
      <div class="sunice-product-form-modal__dialog" role="dialog" aria-modal="true" aria-label="Select options">
        <button type="button" class="sunice-product-form-modal__close" aria-label="Close"></button>
        <div class="sunice-product-form-modal__content"></div>
      </div>
    `;
    document.body.appendChild(modalEl);

    const closeModal = () => {
      modalEl.classList.remove('is-open');
      modalEl.removeAttribute('data-product-id');
      const contentEl = modalEl.querySelector('.sunice-product-form-modal__content');
      if (contentEl instanceof HTMLElement) {
        contentEl.innerHTML = '';
      }
      document.documentElement.classList.remove('sunice-product-form-modal-open');
    };

    const backdrop = modalEl.querySelector('.sunice-product-form-modal__backdrop');
    const closeBtn = modalEl.querySelector('.sunice-product-form-modal__close');
    if (backdrop instanceof HTMLElement) {
      backdrop.addEventListener('click', closeModal);
    }
    if (closeBtn instanceof HTMLElement) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Expose close function globally if needed elsewhere
    globalWindow.closeSuniceProductFormModal = closeModal;
  }

  const modal = modalEl; // For clearer semantics below
  const content = modalEl.querySelector('.sunice-product-form-modal__content');
  if (!(content instanceof HTMLElement)) return;

  const formContainer = productCard.querySelector('.product-form-container');
  if (!(formContainer instanceof HTMLElement)) return;

  // Clone the form container so desktop inline version is untouched
  const cloned = formContainer.cloneNode(true);

  // Reset picker initialization flags inside the cloned content
  const pickerNodes = cloned.querySelectorAll('.sunice-variant-picker');
  pickerNodes.forEach((node) => {
    if (node instanceof HTMLElement) {
      node.removeAttribute('data-initialized');
      node.removeAttribute('data-variant-card-initialized');
      // Remove the close button from variant picker in modal (modal has its own close button)
      const closeButton = node.querySelector('.sunice-variant-picker__close');
      if (closeButton instanceof HTMLElement) {
        closeButton.remove();
      }
    }
  });

  // Remove the selection buttons (Select size / Close) from the modal copy
  const selectionButtons = cloned.querySelector('.sunice-product-card__selection-buttons');
  if (selectionButtons instanceof HTMLElement) {
    selectionButtons.style.display = 'none';
  }

  content.innerHTML = '';
  content.appendChild(cloned);
  
  // Show the modal
  modalEl.classList.add('is-open');
  modalEl.setAttribute('data-product-id', productCard.getAttribute('data-product-id') || '');
  document.documentElement.classList.add('sunice-product-form-modal-open');

  // CRITICAL: Use the existing setupSuniceVariantPicker function - it works great!
  // We just need to ensure the picker doesn't have data-initialized so setupSuniceVariantPicker can find it
  const clonedPicker = cloned.querySelector('.sunice-variant-picker');
  if (clonedPicker instanceof HTMLElement) {
    // Make sure data-initialized is removed so setupSuniceVariantPicker can initialize it
    clonedPicker.removeAttribute('data-initialized');
    
    // Create a temporary product card wrapper so setupSuniceVariantPicker can find it
    // setupSuniceVariantPicker requires a .sunice-product-card parent
    const tempProductCard = document.createElement('div');
    tempProductCard.className = 'sunice-product-card';
    tempProductCard.style.position = 'absolute';
    tempProductCard.style.visibility = 'hidden';
    tempProductCard.style.pointerEvents = 'none';
    tempProductCard.style.width = '0';
    tempProductCard.style.height = '0';
    tempProductCard.style.overflow = 'hidden';
    
    // Move the picker into the temp wrapper temporarily
    const pickerParent = clonedPicker.parentNode;
    tempProductCard.appendChild(clonedPicker);
    document.body.appendChild(tempProductCard);
    
    // Now setupSuniceVariantPicker will find and initialize it
    // CRITICAL: The picker MUST be in the temp wrapper when setupSuniceVariantPicker runs
    // because it checks for .sunice-product-card parent at the start (line 1573-1574)
    // The event listeners are attached to the picker element itself, so they'll persist after moving
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Call setupSuniceVariantPicker - it will find the picker in the temp wrapper
      // This attaches all the event listeners to the picker element itself
      setupSuniceVariantPicker();
      
      // Verify initialization happened (picker should have data-initialized now)
      const isInitialized = clonedPicker.hasAttribute('data-initialized');
      
      // Wait a bit to ensure all listeners are fully attached
      // The listeners are attached synchronously, but give it a frame to be safe
      requestAnimationFrame(() => {
        // Move the picker back to the modal AFTER initialization
        // The event listeners are attached to the picker element itself, so they'll persist
        pickerParent.appendChild(clonedPicker);
        tempProductCard.remove();
        
        // IMMEDIATELY move the button to after the variant picker
        const addToCartContainer = cloned.querySelector('.add-to-cart-container');
        if (addToCartContainer instanceof HTMLElement && clonedPicker instanceof HTMLElement) {
          // Remove from current position
          addToCartContainer.remove();
          // Insert after the variant picker closes
          clonedPicker.insertAdjacentElement('afterend', addToCartContainer);
        }
        
        // If it wasn't initialized, try one more time with a fresh wrapper
        if (!isInitialized) {
          clonedPicker.removeAttribute('data-initialized');
          const tempProductCard2 = document.createElement('div');
          tempProductCard2.className = 'sunice-product-card';
          tempProductCard2.style.position = 'absolute';
          tempProductCard2.style.visibility = 'hidden';
          tempProductCard2.style.pointerEvents = 'none';
          tempProductCard2.style.width = '0';
          tempProductCard2.style.height = '0';
          tempProductCard2.style.overflow = 'hidden';
          const pickerParent2 = clonedPicker.parentNode;
          tempProductCard2.appendChild(clonedPicker);
          document.body.appendChild(tempProductCard2);
          requestAnimationFrame(() => {
            setupSuniceVariantPicker();
            requestAnimationFrame(() => {
              pickerParent2.appendChild(clonedPicker);
              tempProductCard2.remove();
            });
          });
        }
        
        // Set initial variant selection after setup
        requestAnimationFrame(() => {
          setInitialVariantSelection(clonedPicker);
          updateUnavailableStates(clonedPicker);
        });
        
        // Ensure form submission handler works in modal
        const clonedForm = cloned.querySelector('form.product-form');
        if (clonedForm instanceof HTMLFormElement) {
          // Ensure the form has the onsubmit handler (it should be cloned, but verify)
          const productId = clonedForm.getAttribute('data-product-id');
          if (productId && !clonedForm.getAttribute('onsubmit')) {
            clonedForm.setAttribute('onsubmit', `handleShopifyAddToCart(event, ${productId})`);
          }
          
          // Add submit listener to ensure variant ID is synced before submission
          // This runs BEFORE the onsubmit handler to ensure variant ID is set
          clonedForm.addEventListener('submit', function(e) {
            // Ensure variant ID is synced before submission
            const checkedRadios = Array.from(clonedPicker.querySelectorAll('input[type="radio"]:checked'));
            const variantIdInput = clonedForm.querySelector('input[name="id"]');
            
            if (checkedRadios.length >= 2 && variantIdInput instanceof HTMLInputElement) {
              // We have both color and size selected - call handleVariantChange to find the matching variant
              const lastCheckedRadio = checkedRadios[checkedRadios.length - 1];
              if (lastCheckedRadio instanceof HTMLInputElement) {
                handleVariantChange(lastCheckedRadio, clonedPicker);
              }
              
              // Double-check the variant ID was set
              if (!variantIdInput.value || variantIdInput.value.trim() === '') {
                // If handleVariantChange didn't set it, try to find the variant manually
                const colorRadio = checkedRadios.find(r => {
                  const name = r instanceof HTMLInputElement ? r.name : '';
                  return name.includes('Color') || name.includes('Colour');
                });
                const sizeRadio = checkedRadios.find(r => {
                  const name = r instanceof HTMLInputElement ? r.name : '';
                  return name.includes('Size');
                });
                
                if (colorRadio instanceof HTMLInputElement && sizeRadio instanceof HTMLInputElement) {
                  // Try to find variant that matches both color and size
                  const variantsDataScript = clonedPicker.querySelector('.product-variants-data') || 
                                            clonedForm.querySelector('.product-variants-data');
                  if (variantsDataScript instanceof HTMLScriptElement) {
                    try {
                      const productData = JSON.parse(variantsDataScript.textContent || '{}');
                      const variantsList = productData?.variants || [];
                      const colorValue = colorRadio.value;
                      const sizeValue = sizeRadio.value;
                      
                      const matchingVariant = variantsList.find(v => {
                        const variantColor = v.options?.[0];
                        const variantSize = v.options?.[1];
                        return variantColor && variantSize &&
                               variantColor.toString().toLowerCase().trim() === colorValue.toString().toLowerCase().trim() &&
                               variantSize.toString().toLowerCase().trim() === sizeValue.toString().toLowerCase().trim();
                      });
                      
                      if (matchingVariant && variantIdInput) {
                        variantIdInput.value = String(matchingVariant.id);
                      }
                    } catch (error) {
                      // Error parsing product data
                    }
                  }
                }
              }
            } else if (checkedRadios.length > 0 && variantIdInput instanceof HTMLInputElement) {
              // Only one option selected - still try to call handleVariantChange
              const lastCheckedRadio = checkedRadios[checkedRadios.length - 1];
              if (lastCheckedRadio instanceof HTMLInputElement) {
                handleVariantChange(lastCheckedRadio, clonedPicker);
              }
            }
            
            // Final check - if variant ID is still empty, prevent submission
            if (variantIdInput instanceof HTMLInputElement && (!variantIdInput.value || variantIdInput.value.trim() === '')) {
              e.preventDefault();
              alert('Please select all required options before adding to cart.');
              return false;
            }
          }, { capture: true });
        }
        
        // Check if this is a variant card and initialize the picker if needed
        const isVariantCard = productCard.hasAttribute('data-variant-id');
        const variantId = isVariantCard ? productCard.getAttribute('data-variant-id') : null;
        if (isVariantCard && variantId && typeof globalWindow.initializeVariantCardPicker === 'function') {
          setTimeout(async () => {
            await globalWindow.initializeVariantCardPicker(clonedPicker, parseInt(variantId, 10), true);
          }, 150);
        }
      });
    });
    
    // OLD MANUAL SETUP CODE - Keeping as fallback but using setupSuniceVariantPicker above instead
    if (false) {
    // Mark as initialized to prevent setupSuniceVariantPicker from trying to initialize it
    clonedPicker.setAttribute('data-initialized', 'true');
    clonedPicker.setAttribute('data-modal-picker', 'true');
    
    // Reorganize seasonal swatches for this picker (same as desktop)
    if (typeof reorganizeSeasonalSwatchesForPicker === 'function') {
      reorganizeSeasonalSwatchesForPicker(clonedPicker);
    }
    
    // Setup carousel/slider for color swatches (mobile only)
    if (typeof setupVariantPickerCarousel === 'function') {
      setupVariantPickerCarousel(clonedPicker);
    }
    
    // DIRECT LEGEND UPDATE - Update legend on ANY click to color swatch labels or radios (same as desktop)
    // This runs in capture phase to update legend, but doesn't stop propagation
    clonedPicker.addEventListener('click', function(event) {
      const target = event.target;
      // Find color swatch label - check if clicked element or parent is a color swatch
      let label = target.closest('label.color-swatch');
      if (!label && (target.closest('.color-swatch__preview') || target.closest('.swatch'))) {
        label = target.closest('label.color-swatch');
      }
      
      if (label) {
        const radio = label.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement && radio.value) {
          const fieldset = label.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = radio.value;
              } else {
                // Create span if missing
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = radio.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
      
      // Also handle direct radio clicks and any label clicks that contain color swatches
      if (target instanceof HTMLInputElement && target.type === 'radio') {
        const radioLabel = target.closest('label.color-swatch');
        if (radioLabel && target.value) {
          const fieldset = radioLabel.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = target.value;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = target.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
      
      // Handle clicks on the label itself (not the radio)
      const clickedLabel = target.closest('label.color-swatch');
      if (clickedLabel && !(target instanceof HTMLInputElement)) {
        const radio = clickedLabel.querySelector('input[type="radio"]');
        if (radio && radio.value) {
          const fieldset = clickedLabel.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = radio.value;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = radio.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
      // Don't stop propagation - let the main click handler do its work
    }, true); // Use capture phase to fire FIRST
    
    // Handle variant option changes
    clonedPicker.addEventListener('change', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type === 'radio') {
        // Update active states when radio changes
        const label = target.closest('label.sunice-variant-option__button-label');
        const fieldset = target.closest('fieldset');
        
        if (fieldset instanceof HTMLFieldSetElement && label instanceof HTMLElement) {
          // For color swatches (Core or Seasonal), clear active from ALL color swatches
          if (label.classList.contains('color-swatch')) {
            clearAllColorSwatchActiveStates(clonedPicker);
          } else {
            const allLabels = fieldset.querySelectorAll('label.sunice-variant-option__button-label');
            allLabels.forEach(l => {
              l.classList.remove('active');
              l.removeAttribute('data-active');
            });
          }
          
          // Add active class to the selected label
          label.classList.add('active');
          
          // For color swatches, also set data-active attribute
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
        }
        
        // Update legend IMMEDIATELY when a color option changes - use checked state
        const clickedFieldset = target.closest('fieldset');
        if (clickedFieldset) {
          const legend = clickedFieldset.querySelector('legend.core-legend, legend');
          if (legend) {
            // Find the checked radio - this is the source of truth
            const checkedRadio = clickedFieldset.querySelector('input[type="radio"]:checked');
            const valueToShow = checkedRadio ? checkedRadio.value : target.value;
            
            if (valueToShow) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = valueToShow;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = valueToShow;
                legend.appendChild(newSpan);
              }
            }
          }
        }
        
        // Update legends immediately on change
        if (typeof updateProductCardLegends === 'function') {
          updateProductCardLegends(picker);
        }
        
        // Reorganize seasonal swatches after change
        if (typeof reorganizeSeasonalSwatchesForPicker === 'function') {
          reorganizeSeasonalSwatchesForPicker(picker);
        }
        
        handleVariantChange(target, picker);
        // Ensure active states are updated immediately
        updateActiveStates(clonedPicker);
      }
    });

    // Handle click events on labels - THIS IS THE KEY HANDLER FOR VARIANT SELECTION
    // This runs in bubble phase (after capture phase legend update)
    clonedPicker.addEventListener('click', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      
      // Find the label that was clicked (could be the label itself or a child)
      let label = target.closest('label.sunice-variant-option__button-label');
      
      // If clicking on a child element, find the parent label
      if (!label) {
        if (target.classList.contains('sunice-variant-option__button-label__text') || 
            target.classList.contains('color-swatch__preview') ||
            target.classList.contains('swatch')) {
          label = target.closest('label');
        }
      }
      
      // Also handle direct radio button clicks
      if (!label && target instanceof HTMLInputElement && target.type === 'radio') {
        label = target.closest('label.sunice-variant-option__button-label');
      }
      
      if (!(label instanceof HTMLElement)) return;
      
      // Skip if label is unavailable (has pointer-events: none in CSS)
      if (label.classList.contains('unavailable')) {
        return;
      }
      
      const radio = label.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement) || radio.disabled) {
        return;
      }
      
      // Get the fieldset to uncheck other radios in the same group
      const fieldset = label.closest('fieldset');
      if (!(fieldset instanceof HTMLFieldSetElement)) return;
      
      const allRadiosInGroup = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const wasChecked = radio.checked;
      
      // Uncheck all other radios in this group
      allRadiosInGroup.forEach((r) => {
        if (r instanceof HTMLInputElement && r !== radio) {
          r.checked = false;
        }
      });
      
      // Check this radio
      radio.checked = true;
      
      // Update active classes: for color swatches, clear ALL (Core + Seasonal)
      if (label.classList.contains('color-swatch')) {
        clearAllColorSwatchActiveStates(clonedPicker);
      } else {
        const allLabels = fieldset.querySelectorAll('label.sunice-variant-option__button-label');
        allLabels.forEach(l => {
          l.classList.remove('active');
          l.removeAttribute('data-active');
        });
      }
      
      label.classList.add('active');
      if (label.classList.contains('color-swatch')) {
        label.setAttribute('data-active', 'true');
      }
      
      // Get the option name to determine what was clicked
      const productId = clonedPicker.dataset.productId || '';
      const optionName = radio.name.replace(`-${productId}`, '');
      const isColorOption = optionName === 'Color' || optionName === 'Colour';
      const isColorSwatch = label.classList.contains('color-swatch');
      
      // Trigger change event if radio state changed (ensures change handler fires)
      if (!wasChecked) {
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Call handleVariantChange to update variant ID and other state
      // These functions should be in scope since they're defined in this file
      try {
        handleVariantChange(radio, clonedPicker);
        updateActiveStates(clonedPicker);
        
        // Update availability for other options based on selection
        requestAnimationFrame(() => {
          if (isColorSwatch || isColorOption) {
            updateAvailabilityForColorSelection(clonedPicker, radio.value);
          } else {
            updateAvailabilityForSizeSelection(clonedPicker, optionName, radio.value);
          }
          // Update active states again after availability changes
          updateActiveStates(clonedPicker);
        });
      } catch (error) {
        // Error handling variant change
      }
    }, false); // Use bubble phase (after capture phase)
    } // End of fallback code block
    
    // Ensure form submission handler works in modal
    const clonedForm = cloned.querySelector('form.product-form');
    if (clonedForm instanceof HTMLFormElement) {
      // Ensure the form has the onsubmit handler (it should be cloned, but verify)
      const productId = clonedForm.getAttribute('data-product-id');
      if (productId && !clonedForm.getAttribute('onsubmit')) {
        clonedForm.setAttribute('onsubmit', `handleShopifyAddToCart(event, ${productId})`);
      }
      
      // Add submit listener to ensure variant ID is synced before submission
      clonedForm.addEventListener('submit', function(e) {
        // Ensure variant ID is synced before submission
        const checkedRadios = clonedPicker.querySelectorAll('input[type="radio"]:checked');
        if (checkedRadios.length > 0) {
          const lastCheckedRadio = Array.from(checkedRadios)[checkedRadios.length - 1];
          if (lastCheckedRadio instanceof HTMLInputElement) {
            handleVariantChange(lastCheckedRadio, clonedPicker);
          }
        }
      }, { capture: true });
    }
    
    // Set initial variant selection
    requestAnimationFrame(() => {
      setInitialVariantSelection(clonedPicker);
      updateUnavailableStates(clonedPicker);
    });
    
    // Check if this is a variant card and initialize the picker if needed
    const isVariantCard = productCard.hasAttribute('data-variant-id');
    const variantId = isVariantCard ? productCard.getAttribute('data-variant-id') : null;
    if (isVariantCard && variantId && typeof globalWindow.initializeVariantCardPicker === 'function') {
      // Wait a bit for the modal to be fully rendered, then initialize the cloned picker
      setTimeout(async () => {
        await globalWindow.initializeVariantCardPicker(clonedPicker, parseInt(variantId, 10), true);
      }, 150);
    }
  }

  // Ensure initial variant selection when modal opens
  if (clonedPicker instanceof HTMLElement) {
    // Wait for initialization to complete, then set initial selections
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setInitialVariantSelection(clonedPicker);
        updateUnavailableStates(clonedPicker);
      });
    });
  }

  // Re-initialize variant title handlers for the cloned form in the modal
  // This ensures color swatch clicks in the modal update the variant-title in the original product card
  try {
    // Find color swiper containers in the cloned form
    const clonedColorSwipers = cloned.querySelectorAll('.color-swiper-container');
    clonedColorSwipers.forEach((swiperContainer) => {
      if (swiperContainer instanceof HTMLElement) {
        // Set up variant title updates, but target the original product card
        const colorSwatches = swiperContainer.querySelectorAll('.color-swatch');
        if (colorSwatches.length > 0) {
          const variantTitle = productCard.querySelector('.variant-title');
          if (variantTitle) {
            // Set initial variant title
            const activeSwatch = Array.from(colorSwatches).find(s => 
              s.getAttribute('data-active') === 'true'
            ) || Array.from(colorSwatches).find(s => 
              s.getAttribute('data-option-available') === 'true'
            );
            
            if (activeSwatch) {
              const colorName = activeSwatch.getAttribute('data-color') || 
                               activeSwatch.getAttribute('title') || '';
              if (colorName) {
                variantTitle.textContent = colorName;
              }
            }
            
            // Add click handlers to update variant selection AND variant-title
            colorSwatches.forEach((swatch) => {
              const handleColorSwatchClick = function(event) {
                if (event) {
                  event.preventDefault();
                  event.stopPropagation();
                }
                
                // Get color name from the radio button's value inside this swatch, or from data attributes
                const radio = this.querySelector('input[type="radio"]');
                const colorName = radio instanceof HTMLInputElement ? radio.value :
                                 this.getAttribute('data-color') || 
                                 this.getAttribute('title') || '';
                
                if (!colorName) {
                  return;
                }
                
                // Update variant-title in original product card
                if (colorName && variantTitle) {
                  variantTitle.textContent = colorName;
                }
                
                // Update active state on all swatches (both in modal and original card)
                const allSwatches = productCard.querySelectorAll('.color-swatch');
                allSwatches.forEach((s) => {
                  s.removeAttribute('data-active');
                });
                // Also update in modal
                cloned.querySelectorAll('.color-swatch').forEach((s) => {
                  s.removeAttribute('data-active');
                });
                this.setAttribute('data-active', 'true');
                
                // Find and check the corresponding radio button in the modal's variant picker
                const modalPicker = cloned.querySelector('.sunice-variant-picker');
                if (modalPicker instanceof HTMLElement) {
                  const colorFieldsets = Array.from(modalPicker.querySelectorAll('fieldset'));
                  for (const fieldset of colorFieldsets) {
                    if (!(fieldset instanceof HTMLFieldSetElement)) continue;
                    const legend = fieldset.querySelector('legend');
                    const optionName = legend?.textContent?.trim();
                    if (optionName && (optionName.toLowerCase() === 'color' || optionName.toLowerCase() === 'colour')) {
                      // Find the radio button with matching value
                      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
                      for (const radio of radioButtons) {
                        if (!(radio instanceof HTMLInputElement)) continue;
                        const normalizedRadioValue = radio.value.toLowerCase().trim();
                        const normalizedColorValue = colorName.toLowerCase().trim();
                        if (normalizedRadioValue === normalizedColorValue) {
                          // Uncheck all other radios in this fieldset first
                          radioButtons.forEach((r) => {
                            if (r instanceof HTMLInputElement && r !== radio) {
                              r.checked = false;
                              const rLabel = r.closest('label');
                              if (rLabel instanceof HTMLElement) {
                                rLabel.classList.remove('active');
                                rLabel.removeAttribute('data-active');
                              }
                            }
                          });
                          
                          // Get the label for this radio
                          const radioLabel = radio.closest('label.sunice-variant-option__button-label');
                          if (radioLabel instanceof HTMLElement) {
                            // Uncheck all other radios in this fieldset
                            radioButtons.forEach((r) => {
                              if (r instanceof HTMLInputElement && r !== radio) {
                                r.checked = false;
                                const rLabel = r.closest('label');
                                if (rLabel instanceof HTMLElement) {
                                  rLabel.classList.remove('active');
                                  rLabel.removeAttribute('data-active');
                                }
                              }
                            });
                            
                            // Check this radio
                            radio.checked = true;
                            
                            // Update active class
                            const fieldset = radioLabel.closest('fieldset');
                            if (fieldset instanceof HTMLFieldSetElement) {
                              const allLabels = fieldset.querySelectorAll('label');
                              allLabels.forEach(l => {
                                l.classList.remove('active');
                                l.removeAttribute('data-active');
                              });
                            }
                            radioLabel.classList.add('active');
                            if (radioLabel.classList.contains('color-swatch')) {
                              radioLabel.setAttribute('data-active', 'true');
                            }
                            
                            // Call handleVariantChange to update variant ID
                            handleVariantChange(radio, modalPicker);
                            
                            // Call updateAvailabilityForColorSelection in requestAnimationFrame (exact same as desktop)
                            requestAnimationFrame(() => {
                              updateAvailabilityForColorSelection(modalPicker, radio.value);
                            });
                          }
                          break;
                        }
                      }
                    }
                  }
                }
              };
              
              // Remove any existing listeners to avoid duplicates
              const newSwatch = swatch.cloneNode(true);
              swatch.parentNode?.replaceChild(newSwatch, swatch);
              
              // Add both click and touch listeners for mobile support
              newSwatch.addEventListener('click', handleColorSwatchClick);
              newSwatch.addEventListener('touchend', function(e) {
                e.preventDefault();
                handleColorSwatchClick.call(this, e);
              }, { passive: false });
            });
          }
        }
      }
    });
    
    // Also handle non-swiper color swatches in the modal
    const clonedColorSwatches = cloned.querySelectorAll('.color-swatch:not(.color-swiper-container .color-swatch)');
    if (clonedColorSwatches.length > 0) {
      const variantTitle = productCard.querySelector('.variant-title');
      if (variantTitle) {
        clonedColorSwatches.forEach((swatch) => {
          const handleColorSwatchClick = function(event) {
            if (event) {
              event.preventDefault();
              event.stopPropagation();
            }
            
            // Get color name from the radio button's value inside this label
            const radio = this.querySelector('input[type="radio"]');
            const colorName = radio instanceof HTMLInputElement ? radio.value : 
                             this.getAttribute('data-color') || 
                             this.getAttribute('title') || '';
            
            if (!colorName) {
              return;
            }
            
            // Update variant-title in original product card
            if (colorName && variantTitle) {
              variantTitle.textContent = colorName;
            }
            
            // Update active state
            productCard.querySelectorAll('.color-swatch').forEach((s) => {
              s.removeAttribute('data-active');
            });
            cloned.querySelectorAll('.color-swatch').forEach((s) => {
              s.removeAttribute('data-active');
            });
            this.setAttribute('data-active', 'true');
            
            // Find and check the corresponding radio button in the modal's variant picker
            const modalPicker = cloned.querySelector('.sunice-variant-picker');
            if (modalPicker instanceof HTMLElement && radio instanceof HTMLInputElement) {
              // Find the fieldset that contains this radio button (it's the color fieldset)
              const colorFieldset = radio.closest('fieldset');
              
              if (colorFieldset instanceof HTMLFieldSetElement) {
                // Use the radio button we found in the clicked label
                const radioButtons = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
                
                // Uncheck all other radios in this fieldset first
                radioButtons.forEach((r) => {
                  if (r instanceof HTMLInputElement && r !== radio) {
                    r.checked = false;
                    const rLabel = r.closest('label');
                    if (rLabel instanceof HTMLElement) {
                      rLabel.classList.remove('active');
                      rLabel.removeAttribute('data-active');
                    }
                  }
                });
                
                // Check this radio
                radio.checked = true;
                
                // Update active class
                const radioLabel = radio.closest('label.sunice-variant-option__button-label');
                if (radioLabel instanceof HTMLElement) {
                  const fieldsetEl = radioLabel.closest('fieldset');
                  if (fieldsetEl instanceof HTMLFieldSetElement) {
                    const allLabels = fieldsetEl.querySelectorAll('label');
                    allLabels.forEach(l => {
                      l.classList.remove('active');
                      l.removeAttribute('data-active');
                    });
                  }
                  radioLabel.classList.add('active');
                  if (radioLabel.classList.contains('color-swatch')) {
                    radioLabel.setAttribute('data-active', 'true');
                  }
                  
                  // Call handleVariantChange to update variant ID
                  handleVariantChange(radio, modalPicker);
                  
                  // Call updateAvailabilityForColorSelection in requestAnimationFrame (exact same as desktop)
                  requestAnimationFrame(() => {
                    updateAvailabilityForColorSelection(modalPicker, radio.value);
                  });
                }
              }
            }
          };
          
          const newSwatch = swatch.cloneNode(true);
          swatch.parentNode?.replaceChild(newSwatch, swatch);
          
          newSwatch.addEventListener('click', handleColorSwatchClick);
          newSwatch.addEventListener('touchend', function(e) {
            e.preventDefault();
            handleColorSwatchClick.call(this, e);
          }, { passive: false });
        });
      }
    }
  } catch (e) {
    // Failed to initialize variant title handlers in modal
  }

  modal.setAttribute('data-product-id', productCard.dataset.productId || '');
  modal.classList.add('is-open');
  document.documentElement.classList.add('sunice-product-form-modal-open');
}

/**
 * Clears active state from ALL color swatches in the picker (both Core and Seasonal).
 * When a Core swatch is clicked, Seasonal active must be cleared and vice versa.
 * @param {HTMLElement} picker - The sunice-variant-picker element
 */
function clearAllColorSwatchActiveStates(picker) {
  if (!(picker instanceof HTMLElement)) return;
  const colorSwatches = picker.querySelectorAll('label.color-swatch');
  colorSwatches.forEach((swatch) => {
    swatch.classList.remove('active');
    swatch.removeAttribute('data-active');
  });
}

// Reorganize seasonal swatches for product card variant pickers
function reorganizeSeasonalSwatchesForPicker(picker) {
  if (!(picker instanceof HTMLElement)) return;
  
  // Find all seasonal swatches in this picker
  const allSeasonalSwatches = Array.from(picker.querySelectorAll('label[data-seasonal="true"], .seasonal-variant-hidden'));
  
  if (allSeasonalSwatches.length === 0) return;
  
  // Find or create the seasonal fieldset
  let seasonalFieldset = picker.querySelector('.seasonal-variant-section');
  const coreFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => 
    !fs.classList.contains('seasonal-variant-section') && 
    fs.querySelector('.color-swatch')
  );
  
  if (!seasonalFieldset) {
    // Create a new fieldset for Seasonal swatches
    seasonalFieldset = document.createElement('fieldset');
    seasonalFieldset.className = 'variant-option variant-option--buttons seasonal-variant-section';
    
    // Create legend for Seasonal with selected value span always present
    const seasonalLegend = document.createElement('legend');
    seasonalLegend.textContent = 'Seasonal:';
    const seasonalValueSpan = document.createElement('span');
    seasonalValueSpan.className = 'variant-option__selected-value';
    seasonalLegend.appendChild(seasonalValueSpan);
    seasonalFieldset.appendChild(seasonalLegend);
    
    // Create buttons container
    const seasonalButtons = document.createElement('div');
    seasonalButtons.className = 'variant-option__buttons';
    seasonalFieldset.appendChild(seasonalButtons);
    
    // Insert the new Seasonal fieldset BEFORE the Core fieldset (or at the beginning of the picker)
    if (coreFieldset) {
      coreFieldset.parentNode.insertBefore(seasonalFieldset, coreFieldset);
    } else {
      picker.insertBefore(seasonalFieldset, picker.firstChild);
    }
  }
  
  const seasonalButtons = seasonalFieldset.querySelector('.variant-option__buttons');
  if (!seasonalButtons) return;
  
  // Collect all seasonal swatches that need to be moved (not already in seasonal fieldset)
  const swatchesToMove = allSeasonalSwatches.filter(swatch => {
    // Don't move if already in seasonal fieldset
    if (seasonalFieldset.contains(swatch)) return false;
    
    // Remove the temporary hide class
    swatch.classList.remove('seasonal-variant-hidden');
    
    // Ensure data-seasonal attribute is set
    swatch.setAttribute('data-seasonal', 'true');
    const input = swatch.querySelector('input[type="radio"]');
    if (input) {
      input.setAttribute('data-seasonal', 'true');
    }
    
    return true;
  });
  
  // Move swatches that aren't already in the seasonal fieldset
  if (swatchesToMove.length > 0) {
    swatchesToMove.forEach(swatch => {
      // Ensure swatch is always visible, even if out of stock
      swatch.style.display = '';
      swatch.removeAttribute('hidden');
      
      seasonalButtons.appendChild(swatch);
    });
  }
  
  // Ensure all seasonal swatches in the fieldset remain visible (even if out of stock)
  // AND especially if they are active/selected
  const allSeasonalInFieldset = seasonalButtons.querySelectorAll('label');
  allSeasonalInFieldset.forEach(swatch => {
    const input = swatch.querySelector('input[type="radio"]');
    const isActive = input?.checked || swatch.hasAttribute('data-active') || swatch.classList.contains('active');
    
    swatch.style.display = '';
    swatch.removeAttribute('hidden');
    swatch.setAttribute('data-seasonal', 'true');
    
    // If active, ensure it's always visible and clickable
    if (isActive) {
      swatch.style.visibility = 'visible';
      swatch.style.opacity = '1';
      swatch.style.pointerEvents = 'auto';
      swatch.classList.remove('unavailable');
      swatch.removeAttribute('data-unavailable');
      if (input) {
        input.disabled = false;
      }
    }
  });
  
  // Clear all color swatch active states, then restore only for the checked color swatch
  clearAllColorSwatchActiveStates(picker);
  const checkedColorRadio = picker.querySelector('label.color-swatch input[type="radio"]:checked');
  if (checkedColorRadio) {
    const checkedLabel = checkedColorRadio.closest('label.color-swatch');
    if (checkedLabel) {
      checkedLabel.classList.add('active');
      checkedLabel.setAttribute('data-active', 'true');
    }
  }

  // Update legends for seasonal and core
  updateProductCardLegends(picker);
}

// Setup carousel/slider for variant picker color swatches (mobile only, similar to PDP)
function setupVariantPickerCarousel(picker) {
  if (!(picker instanceof HTMLElement)) return;
  
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  if (!isMobile) return; // Only on mobile
  
  // Find color swatch fieldsets
  const colorFieldsets = Array.from(picker.querySelectorAll('fieldset.variant-option--buttons')).filter(fs => {
    const legend = fs.querySelector('legend');
    return legend && (legend.textContent.includes('Core:') || legend.textContent.includes('Seasonal:'));
  });
  
  colorFieldsets.forEach((fieldset) => {
    if (!(fieldset instanceof HTMLElement)) return;
    
    // Check if carousel already exists
    const existingCarousel = fieldset.querySelector('[data-variant-swatches-carousel="true"]');
    if (existingCarousel) return; // Already set up
    
    // Get all color swatch labels
    const swatchLabels = Array.from(fieldset.querySelectorAll('label.color-swatch'));
    if (swatchLabels.length === 0) return;
    
    // Get the legend
    const legend = fieldset.querySelector('legend');
    if (!legend) return;
    
    // Create carousel structure
    const carousel = document.createElement('div');
    carousel.className = 'pdp-variant-swatches-carousel';
    carousel.setAttribute('data-variant-swatches-carousel', 'true');
    
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'pdp-variant-swatches-carousel__arrow pdp-variant-swatches-carousel__arrow--prev';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.innerHTML = `<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.820312 12.8199L6.82031 6.81995L0.820312 0.819946" stroke="#231F20" stroke-width="1.64" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
    
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'pdp-variant-swatches-carousel__arrow pdp-variant-swatches-carousel__arrow--next';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.innerHTML = `<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.820312 12.8199L6.82031 6.81995L0.820312 0.819946" stroke="#231F20" stroke-width="1.64" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
    
    const slidesEl = document.createElement('div');
    slidesEl.setAttribute('data-variant-swatches-slides', 'true');
    slidesEl.className = 'pdp-variant-swatches-carousel__slides';
    
    // Move all swatch labels into the slides container
    swatchLabels.forEach(label => {
      label.style.scrollSnapAlign = 'start';
      slidesEl.appendChild(label);
    });
    
    // Setup scroll handlers
    prevBtn.addEventListener('click', () => {
      const first = slidesEl.firstElementChild;
      const delta = first instanceof HTMLElement ? first.offsetWidth + 16 : 120;
      slidesEl.scrollBy({ left: -delta, behavior: 'smooth' });
    });
    
    nextBtn.addEventListener('click', () => {
      const first = slidesEl.firstElementChild;
      const delta = first instanceof HTMLElement ? first.offsetWidth + 16 : 120;
      slidesEl.scrollBy({ left: delta, behavior: 'smooth' });
    });
    
    carousel.appendChild(prevBtn);
    carousel.appendChild(slidesEl);
    carousel.appendChild(nextBtn);
    
    // Insert carousel after legend
    legend.insertAdjacentElement('afterend', carousel);
    
    // Update arrow visibility based on overflow
    const updateArrowState = () => {
      // Force reflow to get accurate measurements
      void carousel.offsetHeight;
      void slidesEl.offsetHeight;
      
      // Ensure we have accurate measurements
      const scrollWidth = slidesEl.scrollWidth;
      const clientWidth = slidesEl.clientWidth;
      const scrollLeft = slidesEl.scrollLeft;
      
      // More lenient overflow detection (account for padding/margins)
      const isOverflowing = scrollWidth > clientWidth + 5; // Increased tolerance
      const atStartNow = scrollLeft <= 5;
      const atEndNow = scrollLeft + clientWidth >= scrollWidth - 5;
      
      // Remove static class if overflowing, add it if not
      if (isOverflowing) {
        carousel.classList.remove('is-static');
        // Ensure arrows are visible by default when overflowing
        if (prevBtn instanceof HTMLElement) {
          prevBtn.style.display = 'flex';
          prevBtn.style.visibility = 'visible';
          prevBtn.style.opacity = '1';
        }
        if (nextBtn instanceof HTMLElement) {
          nextBtn.style.display = 'flex';
          nextBtn.style.visibility = 'visible';
          nextBtn.style.opacity = '1';
        }
      } else {
        carousel.classList.add('is-static');
      }
      
      // Show/hide arrows based on overflow and scroll position
      if (prevBtn instanceof HTMLElement) {
        const hidePrev = !isOverflowing || atStartNow;
        if (hidePrev) {
          prevBtn.classList.add('is-hidden');
          prevBtn.style.display = 'none';
        } else {
          prevBtn.classList.remove('is-hidden');
          prevBtn.style.display = 'flex';
          prevBtn.style.visibility = 'visible';
          prevBtn.style.opacity = '1';
          prevBtn.style.pointerEvents = 'auto';
        }
        prevBtn.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
      }
      if (nextBtn instanceof HTMLElement) {
        const hideNext = !isOverflowing || atEndNow;
        if (hideNext) {
          nextBtn.classList.add('is-hidden');
          nextBtn.style.display = 'none';
        } else {
          nextBtn.classList.remove('is-hidden');
          nextBtn.style.display = 'flex';
          nextBtn.style.visibility = 'visible';
          nextBtn.style.opacity = '1';
          nextBtn.style.pointerEvents = 'auto';
        }
        nextBtn.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
      }
    };
    
    // Initial check - try multiple times to ensure DOM is fully laid out
    const checkOverflow = () => {
      // Force a reflow to ensure accurate measurements
      void slidesEl.offsetWidth;
      updateArrowState();
    };
    
    // Check immediately
    checkOverflow();
    
    // Check after next frame
    requestAnimationFrame(checkOverflow);
    
    // Check after a short delay (in case images are still loading)
    setTimeout(checkOverflow, 100);
    setTimeout(checkOverflow, 300);
    
    // Update on scroll
    slidesEl.addEventListener('scroll', () => requestAnimationFrame(updateArrowState), { passive: true });
    
    // Update on resize - observe both slidesEl and carousel to catch all size changes
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });
    resizeObserver.observe(slidesEl);
    resizeObserver.observe(carousel);
  });
}

// Update legends for product card variant pickers
/**
 * Updates the product card link URL with the selected variant ID
 * @param {HTMLElement} picker - The variant picker element
 * @param {HTMLInputElement} radio - The selected radio button
 */
function updateProductCardLinkUrl(picker, radio) {
  if (!picker || !radio) return;
  
  // Find the product card
  const productCard = picker.closest('.sunice-product-card, .sunice-variant-product-card');
  if (!productCard) return;
  
  // Get variant ID from radio button data attribute or from the form's hidden input
  let variantId = radio.dataset.variantId || '';
  
  // If no variant ID on radio, try to get from the form's hidden input (which gets updated by handleVariantChange)
  if (!variantId) {
    const form = picker.closest('form');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantId = variantIdInput.value;
      }
    }
  }
  
  // If still no variant ID, try to get from product variants data
  if (!variantId) {
    const variantsDataScript = picker.querySelector('.product-variants-data');
    if (variantsDataScript instanceof HTMLScriptElement) {
      try {
        const productData = JSON.parse(variantsDataScript.textContent || '{}');
        const variantsList = productData?.variants || [];
        
        // Find variant that matches all selected options
        const selectedOptions = {};
        const allRadios = picker.querySelectorAll('input[type="radio"]:checked');
        allRadios.forEach((r) => {
          if (r instanceof HTMLInputElement) {
            const optionName = r.name.replace(`-${picker.dataset.productId}`, '').replace('-swatch', '');
            selectedOptions[optionName] = r.value;
          }
        });
        
        // Find matching variant
        const matchingVariant = variantsList.find((v) => {
          if (!v.options || !Array.isArray(v.options)) return false;
          const optionsList = productData?.options || [];
          return optionsList.every((opt, idx) => {
            const optionName = opt.name;
            return v.options[idx] === selectedOptions[optionName];
          });
        });
        
        if (matchingVariant && matchingVariant.id) {
          variantId = String(matchingVariant.id);
        }
      } catch (e) {
        console.warn('Error parsing variant data:', e);
      }
    }
  }
  
  if (!variantId) return;
  
  // Find all product card links
  const productCardLink = productCard.querySelector('a[href*="/products/"]');
  const productTitleLink = productCard.querySelector('.product-card__title a, .product-title a');
  const cardGalleryLink = productCard.querySelector('.product-card__gallery-link');
  
  // Get base product URL (remove existing variant param)
  const baseUrl = picker.dataset.productUrl || (productCardLink ? productCardLink.href.split('?')[0] : '');
  if (!baseUrl) return;
  
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('variant', variantId);
  
  const newUrl = url.toString();
  
  // Update all product card links
  if (productCardLink instanceof HTMLAnchorElement) {
    productCardLink.href = newUrl;
  }
  if (productTitleLink instanceof HTMLAnchorElement) {
    productTitleLink.href = newUrl;
  }
  if (cardGalleryLink instanceof HTMLAnchorElement) {
    cardGalleryLink.href = newUrl;
  }
}

function updateProductCardLegends(picker) {
  if (!(picker instanceof HTMLElement)) return;
  
  const seasonalFieldset = picker.querySelector('.seasonal-variant-section');
  const coreFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => 
    !fs.classList.contains('seasonal-variant-section') && 
    fs.querySelector('.color-swatch')
  );
  
  // Update Seasonal legend
  if (seasonalFieldset) {
    const seasonalLegend = seasonalFieldset.querySelector('legend');
    if (seasonalLegend) {
      let seasonalValueSpan = seasonalLegend.querySelector('.variant-option__selected-value');
      if (!seasonalValueSpan) {
        seasonalValueSpan = document.createElement('span');
        seasonalValueSpan.className = 'variant-option__selected-value';
        seasonalLegend.appendChild(seasonalValueSpan);
      }
      
      const selectedSeasonal = seasonalFieldset.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
      if (selectedSeasonal) {
        seasonalValueSpan.textContent = selectedSeasonal.value;
      } else {
        const firstSeasonalInput = seasonalFieldset.querySelector('input[type="radio"]');
        if (firstSeasonalInput) {
          seasonalValueSpan.textContent = firstSeasonalInput.value;
        } else {
          seasonalValueSpan.textContent = '';
        }
      }
    }
  }
  
  // Update Core legend
  if (coreFieldset) {
    const coreLegend = coreFieldset.querySelector('legend');
    if (coreLegend) {
      // Clean up legend text - ensure it only contains "Core:" (or whatever the base text is)
      const baseText = 'Core:';
      // Remove any existing text content that includes values after "Core:"
      if (coreLegend.textContent && coreLegend.textContent.trim() !== baseText && !coreLegend.textContent.trim().startsWith(baseText + '<')) {
        // Check if there's already a span
        const existingSpan = coreLegend.querySelector('.variant-option__selected-value');
        if (!existingSpan) {
          // Clean up and set to just base text
          coreLegend.textContent = baseText;
        }
      }
      
      let coreValueSpan = coreLegend.querySelector('.variant-option__selected-value');
      if (!coreValueSpan) {
        coreValueSpan = document.createElement('span');
        coreValueSpan.className = 'variant-option__selected-value';
        // Ensure legend only contains base text before adding span
        if (coreLegend.textContent.trim() !== baseText) {
          coreLegend.textContent = baseText;
        }
        coreLegend.appendChild(coreValueSpan);
      }
      
      // Find checked radio - check multiple ways
      let selectedCore = coreFieldset.querySelector('input[type="radio"]:checked');
      if (!selectedCore) {
        selectedCore = coreFieldset.querySelector('input[type="radio"][checked]');
      }
      // Also check for active label's radio
      if (!selectedCore) {
        const activeLabel = coreFieldset.querySelector('label.color-swatch.active, label.color-swatch[data-active="true"]');
        if (activeLabel) {
          selectedCore = activeLabel.querySelector('input[type="radio"]');
        }
      }
      
      if (selectedCore && selectedCore.value) {
        coreValueSpan.textContent = selectedCore.value;
      } else {
        const firstCoreInput = coreFieldset.querySelector('input[type="radio"]');
        if (firstCoreInput && firstCoreInput.value) {
          coreValueSpan.textContent = firstCoreInput.value;
        } else {
          coreValueSpan.textContent = '';
        }
      }
      
      // Also update any legend outside the picker with the same data-product-id
      const productCard = picker.closest('.sunice-product-card');
      if (productCard) {
        const productId = picker.dataset.productId || productCard.getAttribute('data-product-id');
        if (productId) {
          const outerLegend = productCard.querySelector(`.core-legend[data-product-id="${productId}"]:not(${coreLegend.outerHTML.includes('variant-option--buttons') ? '' : 'fieldset legend'})`);
          if (!outerLegend || outerLegend === coreLegend) {
            // Try to find the legend by checking all core-legends in the product card
            const allCoreLegends = productCard.querySelectorAll(`.core-legend[data-product-id="${productId}"]`);
            allCoreLegends.forEach(legend => {
              if (legend !== coreLegend) {
                let outerValueSpan = legend.querySelector('.variant-option__selected-value');
                if (!outerValueSpan) {
                  outerValueSpan = document.createElement('span');
                  outerValueSpan.className = 'variant-option__selected-value';
                  if (legend.textContent.trim() !== 'Core:') {
                    legend.textContent = 'Core:';
                  }
                  legend.appendChild(outerValueSpan);
                }
                outerValueSpan.textContent = coreValueSpan.textContent;
              }
            });
          }
        }
      }
    }
  }
  
  // Update Size legend
  const sizeFieldset = Array.from(picker.querySelectorAll('.variant-option--buttons')).find(fs => {
    const legend = fs.querySelector('legend');
    return legend && (legend.textContent.includes('Size:') || legend.textContent.includes('size:'));
  });
  
  if (sizeFieldset) {
    const sizeLegend = sizeFieldset.querySelector('legend');
    if (sizeLegend) {
      let sizeValueSpan = sizeLegend.querySelector('.variant-option__selected-value');
      if (!sizeValueSpan) {
        sizeValueSpan = document.createElement('span');
        sizeValueSpan.className = 'variant-option__selected-value';
        sizeLegend.appendChild(sizeValueSpan);
      }
      
      const selectedSize = sizeFieldset.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
      if (selectedSize) {
        const sizeLabel = selectedSize.closest('label');
        if (sizeLabel) {
          const sizeText = sizeLabel.querySelector('.variant-option__button-label__text')?.textContent?.trim() || 
                          sizeLabel.textContent.trim() || 
                          selectedSize.value;
          sizeValueSpan.textContent = sizeText;
        } else {
          sizeValueSpan.textContent = selectedSize.value;
        }
      } else {
        const firstSizeInput = sizeFieldset.querySelector('input[type="radio"]');
        if (firstSizeInput) {
          const firstLabel = firstSizeInput.closest('label');
          if (firstLabel) {
            const sizeText = firstLabel.querySelector('.variant-option__button-label__text')?.textContent?.trim() || 
                            firstLabel.textContent.trim() || 
                            firstSizeInput.value;
            sizeValueSpan.textContent = sizeText;
          } else {
            sizeValueSpan.textContent = firstSizeInput.value;
          }
        } else {
          sizeValueSpan.textContent = '';
        }
      }
    }
  }
}

// Custom Sunice Variant Picker Handler - Theme Editor Compatible
function setupSuniceVariantPicker() {
  const suniceVariantPickers = /** @type {NodeListOf<HTMLElement>} */ (
    document.querySelectorAll('.sunice-variant-picker:not([data-initialized])')
  );

  suniceVariantPickers.forEach((picker, index) => {
    // Mark as initialized to prevent duplicate event listeners
    picker.setAttribute('data-initialized', 'true');
    
    // Get the product card that contains this picker
    const productCard = picker.closest('.sunice-product-card');
    if (!(productCard instanceof HTMLElement)) return;
    
    // Handle close button click (backup handler - delegated handler should catch it first)
    const closeButton = picker.querySelector('.sunice-variant-picker__close');
    if (closeButton instanceof HTMLElement) {
      closeButton.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Mobile: close modal
        if (window.innerWidth < 750) {
          if (typeof globalWindow.closeSuniceProductFormModal === 'function') {
            globalWindow.closeSuniceProductFormModal();
          } else {
            const modalEl = document.querySelector('.sunice-product-form-modal');
            if (modalEl instanceof HTMLElement) {
              modalEl.classList.remove('is-open');
              document.documentElement.classList.remove('sunice-product-form-modal-open');
            }
          }
          return;
        }
        
        // Desktop: close inline form
        if (typeof globalWindow.closeProductForm === 'function') {
          const tempButton = document.createElement('button');
          tempButton.classList.add('close-form-button');
          productCard.appendChild(tempButton);
          globalWindow.closeProductForm(tempButton);
          tempButton.remove();
        } else {
          // Fallback: direct close
          productCard.classList.remove('active');
          const selectButton = productCard.querySelector('.select-size-button');
          const closeFormButton = productCard.querySelector('.close-form-button');
          if (selectButton instanceof HTMLElement) selectButton.style.display = 'inline-block';
          if (closeFormButton instanceof HTMLElement) closeFormButton.style.display = 'none';
          productCard.removeAttribute('data-outside-click-handler');
        }
      }, true); // Use capture phase
    }

    // Reorganize seasonal swatches for this picker
    reorganizeSeasonalSwatchesForPicker(picker);

    const form = picker.closest('form.product-form');
    if (form instanceof HTMLFormElement && !form.hasAttribute('data-sunice-submit-sync')) {
      form.setAttribute('data-sunice-submit-sync', 'true');
      form.addEventListener(
        'submit',
        () => {
          syncSuniceProductCardVariantId(form);
        },
        { capture: true }
      );
    }
    
    // Setup carousel/slider for color swatches in variant picker (mobile only)
    setupVariantPickerCarousel(picker);
    
    // DIRECT LEGEND UPDATE - Update legend on ANY click to color swatch labels or radios
    picker.addEventListener('click', function(event) {
      const target = event.target;
      // Find color swatch label - check if clicked element or parent is a color swatch
      let label = target.closest('label.color-swatch');
      if (!label && (target.closest('.color-swatch__preview') || target.closest('.swatch'))) {
        label = target.closest('label.color-swatch');
      }
      
      if (label) {
        const radio = label.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement && radio.value) {
          const fieldset = label.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = radio.value;
              } else {
                // Create span if missing
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = radio.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
      
      // Also handle direct radio clicks and any label clicks that contain color swatches
      if (target instanceof HTMLInputElement && target.type === 'radio') {
        const radioLabel = target.closest('label.color-swatch');
        if (radioLabel && target.value) {
          const fieldset = radioLabel.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = target.value;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = target.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
      
      // Handle clicks on the label itself (not the radio)
      const clickedLabel = target.closest('label.color-swatch');
      if (clickedLabel && !(target instanceof HTMLInputElement)) {
        const radio = clickedLabel.querySelector('input[type="radio"]');
        if (radio && radio.value) {
          const fieldset = clickedLabel.closest('fieldset');
          if (fieldset) {
            const legend = fieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = radio.value;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = radio.value;
                legend.appendChild(newSpan);
              }
            }
          }
        }
      }
    }, true); // Use capture phase to fire FIRST
    
    // Handle variant option changes
    picker.addEventListener('change', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.type === 'radio') {
        // Update active states when radio changes
        const label = target.closest('label.sunice-variant-option__button-label');
        const fieldset = target.closest('fieldset');
        
        if (fieldset instanceof HTMLFieldSetElement && label instanceof HTMLElement) {
          // For color swatches (Core or Seasonal), clear active from ALL color swatches
          if (label.classList.contains('color-swatch')) {
            clearAllColorSwatchActiveStates(picker);
          } else {
            const allLabels = fieldset.querySelectorAll('label.sunice-variant-option__button-label');
            allLabels.forEach(l => {
              l.classList.remove('active');
              l.removeAttribute('data-active');
            });
          }
          
          // Add active class to the selected label
          label.classList.add('active');
          
          // For color swatches, also set data-active attribute
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
        }
        
        // Update legend IMMEDIATELY when a color option changes - use checked state
        const clickedFieldset = target.closest('fieldset');
        if (clickedFieldset) {
          const legend = clickedFieldset.querySelector('legend.core-legend, legend');
          if (legend) {
            // Find the checked radio - this is the source of truth
            const checkedRadio = clickedFieldset.querySelector('input[type="radio"]:checked');
            const valueToShow = checkedRadio ? checkedRadio.value : target.value;
            
            if (valueToShow) {
              const valueSpan = legend.querySelector('.variant-option__selected-value');
              if (valueSpan) {
                valueSpan.textContent = valueToShow;
              } else {
                const newSpan = document.createElement('span');
                newSpan.className = 'variant-option__selected-value';
                newSpan.textContent = valueToShow;
                legend.appendChild(newSpan);
              }
            }
          }
        }
        
        // Update legends immediately on change
        if (typeof updateProductCardLegends === 'function') {
          updateProductCardLegends(picker);
        }
        
        // Reorganize seasonal swatches after change
        if (typeof reorganizeSeasonalSwatchesForPicker === 'function') {
          reorganizeSeasonalSwatchesForPicker(picker);
        }
        
        handleVariantChange(target, picker);
      }
    });

    // Handle click events on labels to ensure change event fires
    // (Labels automatically check radio buttons, but we ensure the change event is handled)
    // Use event delegation to catch all clicks, including on child elements
    picker.addEventListener('click', function(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      
      // Handle clicks on radio buttons directly
      if (target instanceof HTMLInputElement && target.type === 'radio') {
        const radio = target;
        const label = radio.closest('label.sunice-variant-option__button-label');
        
        if (label instanceof HTMLElement && !label.classList.contains('unavailable') && !radio.disabled) {
          // Get the fieldset to uncheck other radios in the same group
          const fieldset = label.closest('fieldset');
          if (fieldset instanceof HTMLFieldSetElement) {
            const allRadiosInGroup = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
            allRadiosInGroup.forEach((r) => {
              if (r instanceof HTMLInputElement && r !== radio) {
                r.checked = false;
              }
            });
          }
          
          // Ensure radio is checked
          const wasChecked = radio.checked;
          radio.checked = true;
          
          // Update active classes: for color swatches, clear ALL color swatches (Core + Seasonal)
          if (fieldset instanceof HTMLFieldSetElement) {
            if (label.classList.contains('color-swatch')) {
              clearAllColorSwatchActiveStates(picker);
            } else {
              const allLabels = fieldset.querySelectorAll('label.sunice-variant-option__button-label');
              allLabels.forEach(l => {
                l.classList.remove('active');
                l.removeAttribute('data-active');
              });
            }
          }
          
          label.classList.add('active');
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
          
          // ALWAYS update the legend IMMEDIATELY when a color swatch is clicked
          // Find the legend in the same fieldset and update the span
          const clickedFieldset = label.closest('fieldset');
          if (clickedFieldset && label.classList.contains('color-swatch')) {
            const legend = clickedFieldset.querySelector('legend.core-legend, legend');
            if (legend) {
              // Use the radio value - it should be checked by now
              const valueToShow = radio.value || (radio.checked ? radio.value : '');
              if (valueToShow) {
                const valueSpan = legend.querySelector('.variant-option__selected-value');
                if (valueSpan) {
                  valueSpan.textContent = valueToShow;
                } else {
                  // Fallback: create span if it doesn't exist
                  const newSpan = document.createElement('span');
                  newSpan.className = 'variant-option__selected-value';
                  newSpan.textContent = valueToShow;
                  legend.appendChild(newSpan);
                }
              }
            }
          }
          
          // Trigger change event and handle variant change
          if (!wasChecked) {
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          handleVariantChange(radio, picker);
          
          // Update legends immediately on click
          updateProductCardLegends(picker);
          
          // Update availability
          const productId = picker.dataset.productId || '';
          const optionName = radio.name.replace(`-${productId}`, '');
          const isColorOption = optionName === 'Color' || optionName === 'Colour';
          requestAnimationFrame(() => {
            if (isColorOption) {
              updateAvailabilityForColorSelection(picker, radio.value);
            } else {
              updateAvailabilityForSizeSelection(picker, optionName, radio.value);
            }
            // Update legends again after availability updates
            updateProductCardLegends(picker);
          });
        }
        return;
      }
      
      // Handle clicks on labels or their children (text spans, swatches, etc.)
      let label = target.closest('label.sunice-variant-option__button-label');
      
      // If clicking on a child element, find the parent label
      if (!label) {
        if (target.classList.contains('sunice-variant-option__button-label__text') || 
            target.classList.contains('color-swatch__preview') ||
            target.classList.contains('swatch')) {
          label = target.closest('label');
        }
      }
      
      if (label instanceof HTMLElement && label.classList.contains('sunice-variant-option__button-label')) {
        // Skip if label is unavailable (has pointer-events: none in CSS)
        if (label.classList.contains('unavailable')) {
          return;
        }
        
        const radio = label.querySelector('input[type="radio"]');
        if (radio instanceof HTMLInputElement && !radio.disabled) {
          // CRITICAL: Let browser handle radio selection first - don't interfere
          // The label click will naturally check the radio via browser default behavior
          
          // Get the fieldset to uncheck other radios in the same group
          const fieldset = label.closest('fieldset');
          if (fieldset instanceof HTMLFieldSetElement) {
            const allRadiosInGroup = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
            allRadiosInGroup.forEach((r) => {
              if (r instanceof HTMLInputElement && r !== radio) {
                r.checked = false;
              }
            });
          }
          
          // Ensure radio is checked (browser should do this, but be explicit to guarantee it)
          const wasChecked = radio.checked;
          radio.checked = true;
          
          // Force the browser to recognize the change
          if (!wasChecked) {
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // IMMEDIATELY update active class: for color swatches, clear ALL (Core + Seasonal)
          if (fieldset instanceof HTMLFieldSetElement) {
            if (label.classList.contains('color-swatch')) {
              clearAllColorSwatchActiveStates(picker);
            } else {
              const allLabels = fieldset.querySelectorAll('label.sunice-variant-option__button-label');
              allLabels.forEach(l => {
                l.classList.remove('active');
                l.removeAttribute('data-active');
              });
            }
          }
          
          // Add active class to the clicked label
          label.classList.add('active');
          
          // For color swatches, also set data-active attribute
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
          
          // Check if this is a color swatch click
          const isColorSwatch = label.classList.contains('color-swatch');
          
          // Get the option name to determine what was clicked
          const productId = picker.dataset.productId || '';
          const optionName = radio.name.replace(`-${productId}`, '');
          const isColorOption = optionName === 'Color' || optionName === 'Colour';
          
          // ALWAYS update the legend when a color swatch is clicked (seasonal or regular)
          // Get the color name directly from the radio button value
          const colorName = radio.value || '';
          if (isColorOption && colorName) {
            // Find the legend in the SAME fieldset as the clicked swatch
            const clickedFieldset = label.closest('fieldset');
            if (clickedFieldset) {
              const legend = clickedFieldset.querySelector('legend');
              if (legend) {
                let valueSpan = legend.querySelector('.variant-option__selected-value');
                if (!valueSpan) {
                  valueSpan = document.createElement('span');
                  valueSpan.className = 'variant-option__selected-value';
                  // Clean up legend to only contain base text (e.g., "Core:" or "Seasonal:")
                  const baseText = legend.textContent.split(':')[0] + ':';
                  legend.textContent = baseText;
                  legend.appendChild(valueSpan);
                }
                // Update immediately
                valueSpan.textContent = colorName;
              }
            }
          }
          
          // Update legends IMMEDIATELY on click (before other handlers)
          updateProductCardLegends(picker);
          
          // CRITICAL: Update images IMMEDIATELY when color swatch is clicked
          // Do this BEFORE handleVariantChange to ensure images update right away
          if (isColorSwatch || isColorOption) {
            // Find product card - try multiple methods
            let productCard = picker.closest('.sunice-product-card');
            
            // If not found, try finding via form container
            if (!productCard) {
              const formContainer = picker.closest('.product-form-container');
              if (formContainer) {
                productCard = formContainer.closest('.sunice-product-card');
              }
            }
            
            // If still not found, try finding by product ID
            if (!productCard) {
              const productId = picker.dataset.productId;
              if (productId) {
                productCard = document.querySelector('.sunice-product-card[data-product-id="' + productId + '"]');
              }
            }
            
            if (productCard instanceof HTMLElement) {
              // Get primary image URL from radio button
              let imageUrl = radio.getAttribute('data-image-url') || radio.dataset.imageUrl || '';
              
              if (imageUrl) {
              // Find the next image in product ordering for secondary image
              let secondaryImageUrl = '';
              const ordered = getProductImages(productCard);
              if (ordered) {
                const primaryKey = normalizeUrl(imageUrl);
                const idx = ordered.findIndex((u) => normalizeUrl(u) === primaryKey);
                
                if (idx >= 0 && idx < ordered.length - 1) {
                  // Get the image that comes AFTER the primary image
                  secondaryImageUrl = ordered[idx + 1];
                } else if (idx === -1 && ordered.length > 0) {
                  // If primary image not found in array, use first image as fallback
                  // But try to find a better match by checking if any image matches
                  for (let i = 0; i < ordered.length; i++) {
                    const normalizedOrdered = normalizeUrl(ordered[i]);
                    if (normalizedOrdered && primaryKey.includes(normalizedOrdered) || normalizedOrdered.includes(primaryKey)) {
                      if (i < ordered.length - 1) {
                        secondaryImageUrl = ordered[i + 1];
                        break;
                      }
                    }
                  }
                }
              }
              
              // Update images immediately
              updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
              }
              
              // Update variant-title to match selected color
              const variantTitle = productCard.querySelector('.variant-title');
              if (variantTitle instanceof HTMLElement) {
                const colorName = radio.value || '';
                if (colorName) {
                  variantTitle.textContent = colorName;
                }
              }
            }
          }
          
          // Call handleVariantChange to update variant ID and other state
          handleVariantChange(radio, picker);
          
          // Update product card link URL with selected variant
          updateProductCardLinkUrl(picker, radio);
          
          // Then update availability for other options based on selection
          // Use requestAnimationFrame to ensure DOM is fully updated
          requestAnimationFrame(() => {
            if (isColorSwatch || isColorOption) {
              updateAvailabilityForColorSelection(picker, radio.value);
            } else {
              // Size or other option clicked, update color availability
              updateAvailabilityForSizeSelection(picker, optionName, radio.value);
            }
            // Update legends again after availability updates
            updateProductCardLegends(picker);
          });
        }
      }
    });

    // Set initial variant selection - select the first available complete variant
    setInitialVariantSelection(picker);
    
    // Also calculate unavailable states on initialization
    updateUnavailableStates(picker);
  });
}

/**
 * Select every option radio in the picker to match a resolved variant (color, size, inseam, etc.).
 * @param {HTMLElement} picker
 * @param {any} variant
 * @param {Array<any>} optionsList
 */
function selectAllOptionRadiosForVariant(picker, variant, optionsList) {
  if (!variant || !optionsList?.length) return;

  const normalizeValue = (val) => (val || '').toString().toLowerCase().trim();

  optionsList.forEach((option) => {
    if (!option || typeof option.position !== 'number') return;
    const position = option.position - 1;
    const variantValue = variant.options?.[position];
    if (variantValue == null) return;

    const isColorOption = option.name === 'Color' || option.name === 'Colour';
    if (isColorOption) {
      clearAllColorSwatchActiveStates(picker);
    }

    const fieldset =
      picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`) ||
      Array.from(picker.querySelectorAll('fieldset')).find((fs) => {
        const legend = fs.querySelector('legend');
        const legendText = legend?.textContent?.trim() || '';
        if (isColorOption) {
          return (
            legendText.startsWith('Core') ||
            legendText.startsWith('Color') ||
            legendText.startsWith('Colour')
          );
        }
        return legendText.startsWith(option.name);
      });

    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const targetNorm = normalizeValue(variantValue);
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

    radios.forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const isMatch = normalizeValue(radio.value) === targetNorm;
      radio.checked = isMatch;
      const label = radio.closest('label');
      if (!(label instanceof HTMLElement)) return;
      if (isMatch) {
        label.classList.add('active');
        if (label.classList.contains('color-swatch')) {
          label.setAttribute('data-active', 'true');
        }
      } else {
        label.classList.remove('active');
        label.removeAttribute('data-active');
      }
    });
  });
}

// Set initial variant selection by selecting the first available color and size
/**
 * @param {HTMLElement} picker
 */
function setInitialVariantSelection(picker) {
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Check for initial variant ID from the form
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');
  let initialVariantId = null;
  
  if (variantIdInput instanceof HTMLInputElement) {
    // Check for data-initial-variant-id first, then fall back to value
    initialVariantId = variantIdInput.getAttribute('data-initial-variant-id') || variantIdInput.value;
    if (initialVariantId) {
      initialVariantId = String(initialVariantId).trim();
    }
  }

  // Find Color/Colour and Size options
  const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
  const sizeOption = optionsList.find(opt => opt?.name === 'Size');
  
  if (!colorOption || !sizeOption) {
    // Fallback to old behavior if Color/Size options not found
    const firstVariant = variantsList.find((variant) => variant?.available) || variantsList[0];
    if (!firstVariant) return;
    selectVariantOptions(picker, firstVariant, optionsList, variantsList);
    return;
  }

  const colorOptionPosition = colorOption.position - 1;
  const sizeOptionPosition = sizeOption.position - 1;

  // If we have an initial variant ID, use it to select the correct options
  if (initialVariantId) {
    const initialVariant = variantsList.find((v) => String(v.id) === initialVariantId);
    if (initialVariant) {
      selectAllOptionRadiosForVariant(picker, initialVariant, optionsList);

      // Update the hidden variant ID input
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = String(initialVariant.id);
      }

      // Dispatch events and update states
      const initialSelectedOptions = /** @type {Record<string, string>} */ ({});
      optionsList.forEach((option) => {
        if (!option || typeof option.position !== 'number') return;
        const position = option.position - 1;
        if (position < 0) return;
        const optionValue = initialVariant.options?.[position];
        if (optionValue == null) return;
        initialSelectedOptions[option.name] = optionValue;
      });

      const variantColor = initialVariant.options?.[colorOptionPosition];

      dispatchVariantEvents(picker, initialVariant, initialSelectedOptions);
      updateActiveStates(picker);

      // Update availability based on selected color
      if (variantColor) {
        requestAnimationFrame(() => {
          updateAvailabilityForColorSelection(picker, variantColor);
          reorganizeSeasonalSwatchesForPicker(picker);
          updateProductCardLegends(picker);
        });
      }

      reorganizeSeasonalSwatchesForPicker(picker);
      updateProductCardLegends(picker);
      updateProductCardSwatchBackground(picker, initialSelectedOptions);

      return;
    }
  }

  // Find the color to use: prefer the Liquid-checked radio (set by active filter) over first available.
  let firstAvailableColor = null;
  const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`) ||
                       Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                         const legend = fs.querySelector('legend');
                         return legend && (legend.textContent.trim() === 'Color' || legend.textContent.trim() === 'Colour' || legend.textContent.trim().toLowerCase().includes('color'));
                       });
  
  if (colorFieldset instanceof HTMLFieldSetElement) {
    const colorRadios = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
    // Prefer color already checked by server (active filter or default)
    const serverChecked = colorRadios.find(r => r instanceof HTMLInputElement && r.hasAttribute('checked'));
    if (serverChecked instanceof HTMLInputElement) {
      firstAvailableColor = serverChecked.value;
    }
    if (!firstAvailableColor) {
      for (const radio of colorRadios) {
        if (!(radio instanceof HTMLInputElement)) continue;
        const colorValue = radio.value;
        const hasAvailableVariant = variantsList.some(variant => {
          const variantColor = variant.options?.[colorOptionPosition];
          return variantColor && variantColor.toString().toLowerCase().trim() === colorValue.toString().toLowerCase().trim() && variant.available;
        });
        if (hasAvailableVariant) {
          firstAvailableColor = colorValue;
          break;
        }
      }
    }
    if (!firstAvailableColor && colorRadios.length > 0) {
      const firstRadio = colorRadios[0];
      if (firstRadio instanceof HTMLInputElement) {
        firstAvailableColor = firstRadio.value;
      }
    }
  }

  // Find first available size (size that has at least one available variant)
  let firstAvailableSize = null;
  const sizeFieldset = picker.querySelector(`fieldset[data-option-id="option-${sizeOption.position}"]`) ||
                      Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                        const legend = fs.querySelector('legend');
                        return legend && legend.textContent.trim() === 'Size';
                      });
  
  if (sizeFieldset instanceof HTMLFieldSetElement) {
    const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    for (const radio of sizeRadios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const sizeValue = radio.value;
      // Check if this size has at least one available variant
      const hasAvailableVariant = variantsList.some(variant => {
        const variantSize = variant.options?.[sizeOptionPosition];
        return variantSize && variantSize.toString().toLowerCase().trim() === sizeValue.toString().toLowerCase().trim() && variant.available;
      });
      if (hasAvailableVariant) {
        firstAvailableSize = sizeValue;
        break;
      }
    }
    // If no available size found, use first size
    if (!firstAvailableSize && sizeRadios.length > 0) {
      const firstRadio = sizeRadios[0];
      if (firstRadio instanceof HTMLInputElement) {
        firstAvailableSize = firstRadio.value;
      }
    }
  }

  // Select the first available color
  if (firstAvailableColor && colorFieldset instanceof HTMLFieldSetElement) {
    const colorRadios = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
    colorRadios.forEach(radio => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.value.toString().toLowerCase().trim() === firstAvailableColor.toString().toLowerCase().trim()) {
        radio.checked = true;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.add('active');
          if (label.classList.contains('color-swatch')) {
            label.setAttribute('data-active', 'true');
          }
        }
      } else {
        radio.checked = false;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.remove('active');
          label.removeAttribute('data-active');
        }
      }
    });
  }

  // Select the first available size
  if (firstAvailableSize && sizeFieldset instanceof HTMLFieldSetElement) {
    const sizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    sizeRadios.forEach(radio => {
      if (!(radio instanceof HTMLInputElement)) return;
      if (radio.value.toString().toLowerCase().trim() === firstAvailableSize.toString().toLowerCase().trim()) {
        radio.checked = true;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.add('active');
        }
      } else {
        radio.checked = false;
        const label = radio.closest('label');
        if (label instanceof HTMLElement) {
          label.classList.remove('active');
        }
      }
    });
  }

  // Find variant that matches the selected color and size (prefer available; includes inseam tier)
  let matchingVariant = null;
  if (firstAvailableColor && firstAvailableSize) {
    const colorNorm = firstAvailableColor.toString().toLowerCase().trim();
    const sizeNorm = firstAvailableSize.toString().toLowerCase().trim();
    matchingVariant =
      variantsList.find((variant) => {
        const variantColor = variant.options?.[colorOptionPosition];
        const variantSize = variant.options?.[sizeOptionPosition];
        const colorMatch =
          variantColor && variantColor.toString().toLowerCase().trim() === colorNorm;
        const sizeMatch =
          variantSize && variantSize.toString().toLowerCase().trim() === sizeNorm;
        return colorMatch && sizeMatch && variant.available;
      }) ||
      variantsList.find((variant) => {
        const variantColor = variant.options?.[colorOptionPosition];
        const variantSize = variant.options?.[sizeOptionPosition];
        const colorMatch =
          variantColor && variantColor.toString().toLowerCase().trim() === colorNorm;
        const sizeMatch =
          variantSize && variantSize.toString().toLowerCase().trim() === sizeNorm;
        return colorMatch && sizeMatch;
      });
  }

  if (matchingVariant) {
    selectAllOptionRadiosForVariant(picker, matchingVariant, optionsList);
  }

  // If no exact match, try to find first available variant with the selected color
  if (!matchingVariant && firstAvailableColor) {
    matchingVariant = variantsList.find(variant => {
      const variantColor = variant.options?.[colorOptionPosition];
      const colorMatch = variantColor && variantColor.toString().toLowerCase().trim() === firstAvailableColor.toString().toLowerCase().trim();
      return colorMatch && variant.available;
    });
  }

  // Final fallback: first available variant
  if (!matchingVariant) {
    matchingVariant = variantsList.find((variant) => variant?.available) || variantsList[0];
  }

  if (!matchingVariant) return;

  // Update the hidden variant ID input (reuse formElement and form from earlier in function)
  if (variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(matchingVariant.id);
  }

  // Dispatch events and update states
  const initialSelectedOptions = /** @type {Record<string, string>} */ ({});
  optionsList.forEach((option) => {
    if (!option || typeof option.position !== 'number') return;
    const position = option.position - 1;
    if (position < 0) return;
    const optionValue = matchingVariant.options?.[position];
    if (optionValue == null) return;
    initialSelectedOptions[option.name] = optionValue;
  });

  dispatchVariantEvents(picker, matchingVariant, initialSelectedOptions);
  updateActiveStates(picker);

  // Update availability based on selected color
  if (firstAvailableColor) {
    requestAnimationFrame(() => {
      updateAvailabilityForColorSelection(picker, firstAvailableColor);
      // Reorganize seasonal swatches after availability updates
      reorganizeSeasonalSwatchesForPicker(picker);
      updateProductCardLegends(picker);
    });
  }

  // Reorganize seasonal swatches after variant update
  reorganizeSeasonalSwatchesForPicker(picker);
  updateProductCardLegends(picker);

  updateProductCardSwatchBackground(picker, initialSelectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    // When a color filter is active in the URL, the server already rendered the
    // correct variant image via active_color_filter. Skip the init image update
    // to prevent a visual "rebound" from a later-running idle callback.
    const urlHasColorFilter = window.location.search.indexOf('filter.v.option.color') !== -1 ||
                              window.location.search.indexOf('filter.v.option.colour') !== -1;
    if (!urlHasColorFilter) {
      const activeInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
      const { primary: initialPrimaryImageUrl, secondary: initialSecondaryImageUrl } = getActiveVariantImageUrls(
        picker,
        productCard,
        activeInput instanceof HTMLInputElement ? activeInput : null
      );
      updateProductCardPrimaryImage(productCard, initialPrimaryImageUrl, initialSecondaryImageUrl);
    }
  }
}

// Helper function to select variant options (fallback for non-Color/Size products)
/**
 * @param {HTMLElement} picker
 * @param {any} variant
 * @param {Array<any>} optionsList
 * @param {Array<any>} variantsList
 */
function selectVariantOptions(picker, variant, optionsList, variantsList) {
  const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
  fieldsets.forEach((fieldset) => {
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const legend = fieldset.querySelector('legend');
    const optionName = legend?.textContent?.trim();
    if (!optionName) return;

    const option = optionsList.find((opt) => opt?.name === optionName);

    if (option) {
      const optionPosition = option.position - 1; // Convert to 0-based index
      const variantValue = variant.options?.[optionPosition];

      // Find and select the radio button with this value
      const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      radioButtons.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        if (radio.value === variantValue) {
          radio.checked = true;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.add('active');
            if (label.classList.contains('color-swatch')) {
              label.setAttribute('data-active', 'true');
            }
          }
        } else {
          radio.checked = false;
          const label = radio.closest('label');
          if (label instanceof HTMLElement) {
            label.classList.remove('active');
            label.removeAttribute('data-active');
          }
        }
      });
    }
  });

  // Update the hidden variant ID input
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');
  if (variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(variant.id);
  }

  // Dispatch events and update states
  const initialSelectedOptions = /** @type {Record<string, string>} */ ({});
  optionsList.forEach((option) => {
    if (!option || typeof option.position !== 'number') return;
    const position = option.position - 1;
    if (position < 0) return;
    const optionValue = variant.options?.[position];
    if (optionValue == null) return;
    initialSelectedOptions[option.name] = optionValue;
  });

  dispatchVariantEvents(picker, variant, initialSelectedOptions);
  updateActiveStates(picker);
  
  // Reorganize seasonal swatches after variant update
  reorganizeSeasonalSwatchesForPicker(picker);
  updateProductCardLegends(picker);

  updateProductCardSwatchBackground(picker, initialSelectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    const urlHasColorFilter = window.location.search.indexOf('filter.v.option.color') !== -1 ||
                              window.location.search.indexOf('filter.v.option.colour') !== -1;
    if (!urlHasColorFilter) {
      const activeInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
      const { primary: initialPrimaryImageUrl, secondary: initialSecondaryImageUrl } = getActiveVariantImageUrls(
        picker,
        productCard,
        activeInput instanceof HTMLInputElement ? activeInput : null
      );
      updateProductCardPrimaryImage(productCard, initialPrimaryImageUrl, initialSecondaryImageUrl);
    }
  }
}

// Handle variant change for custom picker
/**
 * @param {HTMLInputElement} selectedOption
 * @param {HTMLElement} picker
 */
function handleVariantChange(selectedOption, picker) {
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  const variantIdInput = form?.querySelector('input[name="id"]');

  // Get product variants data - check both in picker and in form
  let variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement) && form) {
    variantsDataScript = form.querySelector('.product-variants-data');
  }
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Get all selected options for this product
  const selectedOptions = /** @type {Record<string, string>} */ ({});
  const radioButtons = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));

  radioButtons.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    // Get option name from radio name (e.g., "Size-123" -> "Size")
    // Use the product ID to properly split the name
    const productId = picker.dataset.productId || '';
    const optionName = radio.name.replace(`-${productId}`, '');
    const optionValue = radio.value;
    selectedOptions[optionName] = optionValue;
  });

  // Find variant that matches ALL selected options
  let matchingVariant = null;

  for (const variant of variantsList) {
    let matches = true;

    // Check if this variant matches all selected options
    for (const optionName in selectedOptions) {
      const option = optionsList.find((opt) => opt?.name === optionName);
      if (!option) continue;

      const optionPosition = option.position - 1; // Convert to 0-based index
      const expectedValue = selectedOptions[optionName];
      const variantValue = variant.options?.[optionPosition];

      // More flexible matching - handle case differences and whitespace
      if (
        variantValue &&
        expectedValue &&
        variantValue.toString().toLowerCase().trim() !== expectedValue.toString().toLowerCase().trim()
      ) {
        matches = false;
        break;
      }
    }

    if (matches) {
      matchingVariant = variant;
      break;
    }
  }

  if (matchingVariant && variantIdInput instanceof HTMLInputElement) {
    variantIdInput.value = String(matchingVariant.id);

    // Dispatch events
    dispatchVariantEvents(picker, matchingVariant, selectedOptions);
  } else {
    // If no exact match found, try to use the data-variant-id from the last checked radio
    // This is useful when only one option is selected (e.g., just color or just size)
    if (variantIdInput instanceof HTMLInputElement && radioButtons.length > 0) {
      const lastRadio = radioButtons[radioButtons.length - 1];
      if (lastRadio instanceof HTMLInputElement && lastRadio.dataset.variantId) {
        variantIdInput.value = lastRadio.dataset.variantId;
      }
    }

    // Fallback: try to find any variant that matches at least one selected option
    let fallbackVariant = null;
    for (const variant of variantsList) {
      for (const optionName in selectedOptions) {
        const option = optionsList.find((opt) => opt?.name === optionName);
        if (!option) continue;

        const optionPosition = option.position - 1;
        const expectedValue = selectedOptions[optionName];
        const variantValue = variant.options?.[optionPosition];

        if (
          variantValue &&
          expectedValue &&
          variantValue.toString().toLowerCase().trim() === expectedValue.toString().toLowerCase().trim()
        ) {
          fallbackVariant = variant;
          break;
        }
      }
      if (fallbackVariant) break;
    }

    // If still no match, use the first variant
    if (!fallbackVariant && variantsList.length > 0) {
      fallbackVariant = variantsList[0];
    }

    if (fallbackVariant && variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = String(fallbackVariant.id);

      // Dispatch events for fallback variant
      dispatchVariantEvents(picker, fallbackVariant, selectedOptions);
    }
  }

  // Always update active and unavailable states, even if no variant matches
  // This ensures that when a color is selected, size availability updates correctly
  updateActiveStates(picker);
  
  // Reorganize seasonal swatches after availability updates
  reorganizeSeasonalSwatchesForPicker(picker);
  updateProductCardLegends(picker);

  updateProductCardSwatchBackground(picker, selectedOptions);

  const productCard = picker.closest('.sunice-product-card');
  if (productCard instanceof HTMLElement) {
    // CRITICAL: Only update images when a COLOR option is selected, NOT for size or other options
    // This matches the variant product card implementation
    if (selectedOption instanceof HTMLInputElement) {
      const productId = picker.dataset.productId || '';
      const optionName = selectedOption.name.replace(`-${productId}`, '');
      const isColorOption = optionName === 'Color' || optionName === 'Colour';
      
      if (isColorOption) {
        // Update card imagery so hover uses the selected variant's secondary image
        // This matches the swiper click handler EXACTLY
        const imageUrl = selectedOption.dataset.imageUrl || "";
        let secondaryImageUrl = selectedOption.dataset.secondaryImageUrl || "";

        // Prefer "next image in product ordering" after the selected variant's primary image
        // This matches the swiper click handler logic EXACTLY
        const ordered = getProductImages(productCard);
        if (ordered) {
          const primaryKey = normalizeUrl(imageUrl);
          const idx = ordered.findIndex((u) => normalizeUrl(u) === primaryKey);
          if (idx >= 0 && ordered.length > 1) {
            // Find the next DIFFERENT image after the primary (skip duplicates)
            for (let i = idx + 1; i < ordered.length; i++) {
              const candidate = ordered[i];
              const candKey = normalizeUrl(candidate);
              // Also avoid picking another variant's primary "a" image as hover.
              // We prefer the immediate "b" follow-up image if your naming follows *_a / *_b.
              if (candKey && candKey !== primaryKey) {
                const looksLikeAnotherPrimary =
                  /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
                if (looksLikeAnotherPrimary) continue;
                secondaryImageUrl = candidate || secondaryImageUrl;
                break;
              }
            }
          }
        }

        // Update images using the same function as swiper click handler
        updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
        return; // Exit early - images are updated for color selection only
      }
    }
    
    // For size or other options, DO NOT update images
    // Images should only change when color is selected
    // The hover effect will show the secondary image from the currently selected color variant
  }
}

/**
 * Sync hidden variant id from the picker's checked radios before add-to-cart.
 * @param {HTMLFormElement} form
 */
function syncSuniceProductCardVariantId(form) {
  const picker = form.querySelector('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return;

  const checkedRadios = picker.querySelectorAll('input[type="radio"]:checked');
  if (checkedRadios.length > 0) {
    const lastCheckedRadio = checkedRadios[checkedRadios.length - 1];
    if (lastCheckedRadio instanceof HTMLInputElement) {
      handleVariantChange(lastCheckedRadio, picker);
    }
  } else {
    setInitialVariantSelection(picker);
  }
}

/**
 * Resolve the Sunice product card form from a submit/click event.
 * @param {Event} event
 * @returns {HTMLFormElement | null}
 */
function resolveSuniceProductCardForm(event) {
  const target = event.target;
  if (target instanceof HTMLFormElement && target.classList.contains('product-form')) {
    return target;
  }
  if (event.currentTarget instanceof HTMLFormElement && event.currentTarget.classList.contains('product-form')) {
    return event.currentTarget;
  }
  if (target instanceof Element) {
    const fromButton = target.closest('.sunice-product-card form.product-form[data-type="add-to-cart-form"]');
    if (fromButton instanceof HTMLFormElement) return fromButton;
  }
  return null;
}

/**
 * Run add-to-cart for a Sunice product card form (shared by click + submit handlers).
 * @param {Event} event
 * @param {string | number} productId
 */
function runSuniceProductCardAddToCart(event, productId) {
  if (typeof event.preventDefault === 'function') {
    event.preventDefault();
  }

  const form = resolveSuniceProductCardForm(event);
  if (!form) return;

  syncSuniceProductCardVariantId(form);

  const variantIdInput = form.querySelector('input[name="id"]');
  if (!(variantIdInput instanceof HTMLInputElement) || !variantIdInput.value.trim()) {
    alert('Please select all required options before adding to cart.');
    return;
  }

  addProductCardToCart(form, productId);
}

/**
 * Delegated capture click on ATC buttons — reliable in swiper loop clones where
 * inline onsubmit + theme add-to-cart-component do not integrate with our fetch flow.
 */
function initSuniceProductCardAddToCartDelegation() {
  if (globalWindow.__suniceProductCardAddToCartDelegated) return;
  globalWindow.__suniceProductCardAddToCartDelegated = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(
        '.sunice-product-card form.product-form[data-type="add-to-cart-form"] button[name="add"]'
      );
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      if (button.type !== 'submit') return;

      const form = button.closest('form');
      if (!(form instanceof HTMLFormElement)) return;

      event.preventDefault();
      event.stopPropagation();

      const productId = form.getAttribute('data-product-id') || '';
      runSuniceProductCardAddToCart(event, productId);
    },
    true
  );
}

// Dispatch variant events for theme compatibility
/**
 * @param {HTMLElement} picker
 * @param {any} variant
 * @param {Record<string, string>} selectedOptions
 */
function dispatchVariantEvents(picker, variant, selectedOptions) {
  // Dispatch variant selected event for theme compatibility
  picker.dispatchEvent(new CustomEvent('variant-selected', {
    detail: {
      variantId: variant.id,
      variant: variant,
      selectedOptions: selectedOptions
    },
    bubbles: true
  }));

  // Also dispatch the theme's expected events
  const variantSelectedEvent = new CustomEvent('variant:selected', {
    detail: {
      id: variant.id,
      variant: variant
    },
    bubbles: true
  });
  picker.dispatchEvent(variantSelectedEvent);

  // Dispatch on the form as well for add-to-cart compatibility
  const form = picker.closest('form');
  if (form) {
    form.dispatchEvent(new CustomEvent('variant:selected', {
      detail: {
        id: variant.id,
        variant: variant
      },
      bubbles: true
    }));
  }

  // Also dispatch on the product card for broader compatibility
  const productCard = picker.closest('.sunice-product-card');
  if (productCard) {
    productCard.dispatchEvent(new CustomEvent('variant:selected', {
      detail: {
        id: variant.id,
        variant: variant
      },
      bubbles: true
    }));
  }
}

// Update active states for visual feedback
/**
 * @param {HTMLElement} picker
 */
function updateActiveStates(picker) {
  // Update active states for radio buttons
  const radioButtons = Array.from(picker.querySelectorAll('input[type="radio"]'));
  radioButtons.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) return;

    if (radio.checked) {
      label.classList.add('active');
      // Set data-active for color swatches
      if (label.classList.contains('color-swatch')) {
        label.setAttribute('data-active', 'true');
      }
    } else {
      label.classList.remove('active');
      // Remove data-active for color swatches
      if (label.classList.contains('color-swatch')) {
        label.removeAttribute('data-active');
      }
    }
  });

  // Update unavailable states based on current selections
  updateUnavailableStates(picker);
}

/**
 * Update availability for other options when a color is selected
 * @param {HTMLElement} picker - The variant picker element
 * @param {string} selectedColorValue - The selected color value
 */
function updateAvailabilityForColorSelection(picker, selectedColorValue) {
  // Get product variants data - script is in form, not inside picker
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  let variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement) && form) {
    variantsDataScript = form.querySelector('.product-variants-data');
  }
  if (!(variantsDataScript instanceof HTMLScriptElement)) {
    return;
  }

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);
  
  // Find the Color/Colour option to get its position
  const colorOption = optionsList.find(opt => 
    opt?.name === 'Color' || opt?.name === 'Colour'
  );
  if (!colorOption) {
    return;
  }
  
  const colorOptionPosition = colorOption.position - 1; // Convert to 0-based index
  
  // Normalize the selected color value for comparison
  const normalizeValue = (val) => {
    if (!val) return '';
    return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
  };
  const normalizedSelectedColor = normalizeValue(selectedColorValue);
  
  // Find all variants that have this color
  const variantsWithThisColor = variantsList.filter(variant => {
    const variantColorValue = variant.options?.[colorOptionPosition];
    const normalizedVariantColor = normalizeValue(variantColorValue);
    return normalizedVariantColor === normalizedSelectedColor;
  });
  
  // For each other option (not Color), update availability
  optionsList.forEach(option => {
    // Skip the Color option itself
    if (option.name === 'Color' || option.name === 'Colour') {
      return;
    }
    
    const optionPosition = option.position - 1; // Convert to 0-based index
    
    // Get all labels for this option
    // Try multiple selectors to find the fieldset
    let fieldset = picker.querySelector(`fieldset[data-option-id="option-${option.position}"]`);
    if (!fieldset) {
      // Fallback: try finding by legend text
      const allFieldsets = Array.from(picker.querySelectorAll('fieldset'));
      fieldset = allFieldsets.find(fs => {
        const legend = fs.querySelector('legend');
        return legend && legend.textContent.trim() === option.name;
      });
    }
    if (!fieldset) {
      return;
    }
    
    // Try to find labels - use multiple selectors for compatibility
    // First try the specific class
    let labels = Array.from(fieldset.querySelectorAll('label.sunice-variant-option__button-label'));
    
    // If none found, try all labels (fallback)
    if (labels.length === 0) {
      labels = Array.from(fieldset.querySelectorAll('label'));
    }
    
    // Filter out color swatch labels (we only want size/other option labels)
    labels = labels.filter(label => !label.classList.contains('color-swatch'));
    
    labels.forEach(label => {
      const radio = label.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) return;
      
      const optionValue = radio.value;
      const normalizedOptionValue = normalizeValue(optionValue);
      
      // Check if there's an available variant with this color + this option value
      const hasAvailableVariant = variantsWithThisColor.some(variant => {
        const variantOptionValue = variant.options?.[optionPosition];
        const normalizedVariantOption = normalizeValue(variantOptionValue);
        const matches = normalizedVariantOption === normalizedOptionValue;
        const isAvailable = variant.available;
        return matches && isAvailable;
      });
      
      // Also check if there's ANY variant (even unavailable) with this combination
      const hasAnyVariant = variantsWithThisColor.some(variant => {
        const variantOptionValue = variant.options?.[optionPosition];
        const normalizedVariantOption = normalizeValue(variantOptionValue);
        return normalizedVariantOption === normalizedOptionValue;
      });
      
      // Update the label's availability state (matching variant-picker.js pattern)
      // Always update availability, even if the radio is checked (to ensure visual consistency)
      if (hasAvailableVariant) {
        label.classList.remove('unavailable');
        label.classList.remove('variant-option__button-label--out-of-stock');
        label.removeAttribute('data-unavailable');
        label.setAttribute('data-option-available', 'true');
        radio.setAttribute('data-option-available', 'true');
        radio.disabled = false;
        radio.removeAttribute('disabled');
      } else if (hasAnyVariant) {
        if (!radio.checked) {
          label.classList.add('unavailable');
          label.classList.add('variant-option__button-label--out-of-stock');
          label.setAttribute('data-unavailable', 'true');
        }
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      } else {
        if (!radio.checked) {
          label.classList.add('unavailable');
          label.classList.add('variant-option__button-label--out-of-stock');
          label.setAttribute('data-unavailable', 'true');
        }
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      }
      
      // Force a style recalculation to ensure CSS updates
      void label.offsetHeight;
    });
  });
  
  // When color is clicked, always select the first non-disabled (available) size
  const sizeOption = optionsList.find(opt => opt?.name === 'Size');
  
  if (sizeOption) {
    const sizeFieldset = picker.querySelector(`fieldset[data-option-id="option-${sizeOption.position}"]`) ||
                         Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                           const legend = fs.querySelector('legend');
                           return legend && legend.textContent.trim() === 'Size';
                         });
    
    if (sizeFieldset instanceof HTMLFieldSetElement) {
      const checkedSizeRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
      const availableSizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"][data-option-available="true"]'));
      const firstAvailableSizeRadio = availableSizeRadios.length > 0 ? availableSizeRadios[0] : null;
      
      // Select first available size: when no size selected, or when current size is disabled for this color
      const shouldSelectFirstAvailable = firstAvailableSizeRadio && (
        !checkedSizeRadio ||
        !(checkedSizeRadio instanceof HTMLInputElement) ||
        checkedSizeRadio.dataset.optionAvailable === 'false' ||
        checkedSizeRadio.closest('label')?.classList.contains('unavailable')
      );
      
      if (shouldSelectFirstAvailable && firstAvailableSizeRadio instanceof HTMLInputElement) {
        setTimeout(() => {
          // Remove active class from all labels in the size fieldset
          const allLabels = sizeFieldset.querySelectorAll('label.sunice-variant-option__button-label');
          allLabels.forEach(label => {
            if (label instanceof HTMLElement) {
              label.classList.remove('active');
              label.removeAttribute('data-active');
            }
          });
          
          // Uncheck all size radios first
          const allSizeRadios = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
          allSizeRadios.forEach(radio => {
            if (radio instanceof HTMLInputElement) {
              radio.checked = false;
            }
          });
          
          // Select the first available (non-disabled) size
          firstAvailableSizeRadio.checked = true;
          const label = firstAvailableSizeRadio.closest('label.sunice-variant-option__button-label');
          if (label instanceof HTMLElement) {
            label.classList.add('active');
          }
          
          handleVariantChange(firstAvailableSizeRadio, picker);
        }, 0);
      } else if (checkedSizeRadio instanceof HTMLInputElement) {
        requestAnimationFrame(() => {
          handleVariantChange(checkedSizeRadio, picker);
        });
      }
    }
  } else {
    // No Size option, just sync variant ID with color selection
    const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`) ||
                         Array.from(picker.querySelectorAll('fieldset')).find(fs => {
                           const legend = fs.querySelector('legend');
                           return legend && (legend.textContent.trim() === 'Color' || legend.textContent.trim() === 'Colour' || legend.textContent.trim().toLowerCase().includes('color'));
                         });
    if (colorFieldset instanceof HTMLFieldSetElement) {
      const selectedColorRadio = colorFieldset.querySelector('input[type="radio"]:checked');
      if (selectedColorRadio instanceof HTMLInputElement) {
        requestAnimationFrame(() => {
          handleVariantChange(selectedColorRadio, picker);
        });
      }
    }
  }
}

/**
 * Update availability for colors when a size (or other non-color option) is selected.
 * Only mark a colour as unavailable if it has NO available variants in any size (fully unavailable).
 * Colours that have at least one size in stock must not get the unavailable class.
 * @param {HTMLElement} picker - The variant picker element
 * @param {string} selectedOptionName - The selected option name (e.g., "Size")
 * @param {string} selectedOptionValue - The selected option value
 */
function updateAvailabilityForSizeSelection(picker, selectedOptionName, selectedOptionValue) {
  const variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement)) return;

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);
  const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
  if (!colorOption) return;

  const colorOptionPosition = colorOption.position - 1;
  const normalizeValue = (val) => {
    if (!val) return '';
    return val.toString().toLowerCase().trim().replace(/\\\//g, '/');
  };

  const colorFieldset = picker.querySelector(`fieldset[data-option-id="option-${colorOption.position}"]`);
  if (!colorFieldset) return;

  const colorLabels = Array.from(colorFieldset.querySelectorAll('label.sunice-variant-option__button-label.color-swatch'));
  colorLabels.forEach((label) => {
    const radio = label.querySelector('input[type="radio"]');
    if (!(radio instanceof HTMLInputElement)) return;

    const colorValue = radio.value;
    const normalizedColorValue = normalizeValue(colorValue);

    // Only mark unavailable if this colour has ZERO available variants (any size) - fully unavailable
    const hasAnyAvailableVariant = variantsList.some((variant) => {
      const variantColorValue = variant.options?.[colorOptionPosition];
      const normalizedVariantColor = normalizeValue(variantColorValue);
      return normalizedVariantColor === normalizedColorValue && variant.available;
    });

    if (hasAnyAvailableVariant) {
      label.classList.remove('unavailable');
      label.classList.remove('variant-option__button-label--out-of-stock');
      label.removeAttribute('data-unavailable');
      label.setAttribute('data-option-available', 'true');
      radio.setAttribute('data-option-available', 'true');
      radio.disabled = false;
      radio.removeAttribute('disabled');
    } else {
      label.classList.add('unavailable');
      label.classList.add('variant-option__button-label--out-of-stock');
      label.setAttribute('data-unavailable', 'true');
      label.setAttribute('data-option-available', 'false');
      radio.setAttribute('data-option-available', 'false');
    }
  });
}

/**
 * Update unavailable states for variant options based on current selections
 * @param {HTMLElement} picker
 */
function updateUnavailableStates(picker) {
  // CRITICAL: Never modify facet swatches - they should stay consistent
  // Check if this picker is inside a facets component (desktop or mobile drawer)
  if (picker.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
    return; // Skip entirely if inside facets
  }
  
  // Get product variants data - script is in form, not inside picker
  const formElement = picker.closest('form');
  const form = formElement instanceof HTMLFormElement ? formElement : null;
  let variantsDataScript = picker.querySelector('.product-variants-data');
  if (!(variantsDataScript instanceof HTMLScriptElement) && form) {
    variantsDataScript = form.querySelector('.product-variants-data');
  }
  if (!(variantsDataScript instanceof HTMLScriptElement)) {
    return;
  }

  let productData;
  try {
    productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
  } catch (error) {
    return;
  }

  const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
  const optionsList = /** @type {Array<any>} */ (productData?.options || []);

  // Get currently selected options
  const selectedOptions = /** @type {Record<string, string>} */ ({});
  const checkedRadios = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));
  const productId = picker.dataset.productId || '';

  checkedRadios.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const optionName = radio.name.replace(`-${productId}`, '');
    selectedOptions[optionName] = radio.value;
  });

  // CRITICAL: Do NOT reset data-option-available attributes that are already set by Liquid
  // The Liquid template correctly sets these based on actual variant availability
  // We should only update them when checking combinations with selected options
  // For initial state (no selections), trust the Liquid values

  // Check each option value to see if it's available with current selections
  const allRadios = Array.from(picker.querySelectorAll('input[type="radio"]'));
  allRadios.forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;
    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) return;
    
    // CRITICAL: Skip facet swatches - never modify them (desktop or mobile drawer)
    if (label.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component') || 
        radio.closest('.facets__item, .facets__panel, .facets--drawer, facets-form-component')) {
      return;
    }

    // Get the option name and value for this radio
    const optionName = radio.name.replace(`-${productId}`, '');
    const optionValue = radio.value;

    // Colour swatches: only mark unavailable if this colour has ZERO available variants (any size). If any size in stock, always show as available.
    if (label.classList.contains('color-swatch') && (optionName === 'Color' || optionName === 'Colour')) {
      const colorOption = optionsList.find(opt => opt?.name === 'Color' || opt?.name === 'Colour');
      if (colorOption) {
        const colorOptionPosition = colorOption.position - 1;
        const normalizeVal = (v) => !v ? '' : v.toString().toLowerCase().trim().replace(/\\\//g, '/');
        const normColor = normalizeVal(optionValue);
        const hasAnyForColor = variantsList.some((v) => {
          const variantColor = v.options?.[colorOptionPosition];
          return normalizeVal(variantColor) === normColor && v.available;
        });
        if (hasAnyForColor) {
          label.classList.remove('unavailable');
          label.classList.remove('variant-option__button-label--out-of-stock');
          label.removeAttribute('data-unavailable');
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
          radio.disabled = false;
          radio.removeAttribute('disabled');
        } else {
          label.classList.add('unavailable');
          label.classList.add('variant-option__button-label--out-of-stock');
          label.setAttribute('data-unavailable', 'true');
          label.setAttribute('data-option-available', 'false');
          radio.setAttribute('data-option-available', 'false');
        }
      }
      return;
    }

    // If no options are selected yet, trust the data-option-available value from Liquid
    // The Liquid template already correctly sets this based on actual variant availability
    if (Object.keys(selectedOptions).length === 0) {
      // Preserve the existing data-option-available value from Liquid
      const existingAvailable = radio.dataset.optionAvailable;
      if (existingAvailable === 'false') {
        label.classList.add('unavailable');
        label.classList.add('variant-option__button-label--out-of-stock');
        label.setAttribute('data-unavailable', 'true');
        label.setAttribute('data-option-available', 'false');
        radio.setAttribute('data-option-available', 'false');
      } else {
        label.classList.remove('unavailable');
        label.classList.remove('variant-option__button-label--out-of-stock');
        label.removeAttribute('data-unavailable');
        if (existingAvailable !== 'true') {
          label.setAttribute('data-option-available', 'true');
          radio.setAttribute('data-option-available', 'true');
        }
        radio.disabled = false;
        radio.removeAttribute('disabled');
      }
      return;
    }

    // Create a test selection with this option value
    const testOptions = { ...selectedOptions, [optionName]: optionValue };

    // Find if there's any variant that matches this combination
    // Check both if any variant exists AND if any is available
    let variantExists = false;
    let hasAvailableVariant = false;
    let matchingVariants = [];
    
    for (const variant of variantsList) {
      let matches = true;

      // Check if this variant matches all test options (only check selected + testing option)
      for (const testOptionName in testOptions) {
        const option = optionsList.find((opt) => opt?.name === testOptionName);
        if (!option) {
          matches = false;
          break;
        }

        const optionPosition = option.position - 1;
        const expectedValue = testOptions[testOptionName];
        const variantValue = variant.options?.[optionPosition];

        if (!expectedValue) {
          matches = false;
          break;
        }

        // Normalize values for comparison (handle escaped slashes, case, whitespace)
        const normalizedExpected = expectedValue.toString().toLowerCase().trim().replace(/\\\//g, '/');
        const normalizedVariant = variantValue ? variantValue.toString().toLowerCase().trim().replace(/\\\//g, '/') : '';

        if (!normalizedVariant || normalizedVariant !== normalizedExpected) {
          matches = false;
          break;
        }
      }

      if (matches) {
        variantExists = true;
        matchingVariants.push({ id: variant.id, available: variant.available, options: variant.options });
        if (variant.available) {
          hasAvailableVariant = true;
          // Don't break - continue checking to see all matching variants for debugging
        }
      }
    }

    // Mark as unavailable only if:
    // 1. No variant exists with this combination, OR
    // 2. Variants exist but ALL are unavailable
    const isUnavailable = !variantExists || (variantExists && !hasAvailableVariant);

    // Update unavailable state (matching variant-picker.js pattern)
    if (isUnavailable) {
      label.classList.add('unavailable');
      label.classList.add('variant-option__button-label--out-of-stock');
      label.setAttribute('data-unavailable', 'true');
      label.setAttribute('data-option-available', 'false');
      radio.setAttribute('data-option-available', 'false');
    } else {
      label.classList.remove('unavailable');
      label.classList.remove('variant-option__button-label--out-of-stock');
      label.removeAttribute('data-unavailable');
      label.setAttribute('data-option-available', 'true');
      radio.setAttribute('data-option-available', 'true');
      radio.disabled = false;
      radio.removeAttribute('disabled');
    }
  });
  
  // Verify the updates were applied
  const unavailableCount = picker.querySelectorAll('label[data-unavailable="true"]').length;
  const unavailableSizes = Array.from(picker.querySelectorAll('label[data-unavailable="true"]'))
    .map(label => {
      const radio = label.querySelector('input[type="radio"]');
      return radio instanceof HTMLInputElement ? radio.value : null;
    })
    .filter(Boolean);
}

/**
 * @param {HTMLElement} preview
 * @returns {string}
 */
function extractSwatchBackground(preview) {
  const inlineValue = preview.style.getPropertyValue('--swatch-background')?.trim();
  if (inlineValue) return inlineValue;

  const dataValue = preview.dataset?.swatchBackground?.trim();
  if (dataValue) return dataValue;

  const computedValue = window.getComputedStyle(preview).getPropertyValue('--swatch-background')?.trim();
  return computedValue || '';
}

/**
 * @param {HTMLElement} picker
 * @param {string} optionName
 * @param {string} optionValue
 * @returns {HTMLElement | null}
 */
function findColorSwatchPreview(picker, optionName, optionValue) {
  if (!optionName || optionValue == null) return null;

  const normalizedOptionName = optionName.toLowerCase();
  const normalizedOptionValue = optionValue.toString().toLowerCase().trim();
  const productId = picker.dataset.productId || '';

  const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
  for (const radio of radios) {
    if (!(radio instanceof HTMLInputElement)) continue;
    const radioName = radio.name.replace(`-${productId}`, '').toLowerCase();
    if (radioName !== normalizedOptionName) continue;

    const radioValue = radio.value.toLowerCase().trim();
    if (radioValue !== normalizedOptionValue) continue;

    const label = radio.closest('label');
    if (!(label instanceof HTMLElement)) continue;

    const preview = label.querySelector('.color-swatch__preview, .swatch');
    if (preview instanceof HTMLElement) {
      return preview;
    }
  }

  return null;
}

/**
 * @param {HTMLElement} picker
 * @param {Record<string, string>} selectedOptions
 * @returns {void}
 */
function updateProductCardSwatchBackground(picker, selectedOptions) {
  if (!(picker instanceof HTMLElement)) return;

  const productCard = picker.closest('.sunice-product-card');
  if (!(productCard instanceof HTMLElement)) return;

  const productFormContainerElement = productCard.querySelector('.product-form-container');
  const productFormContainer = productFormContainerElement instanceof HTMLElement ? productFormContainerElement : null;

  /**
   * @param {HTMLElement | null} element
   * @param {string} value
   */
  const applyBackground = (element, value) => {
    if (!(element instanceof HTMLElement)) return;
    if (value) {
      element.style.setProperty('--selected-swatch-background', value);
      element.dataset.selectedSwatchBackground = value;
    } else {
      element.style.removeProperty('--selected-swatch-background');
      delete element.dataset.selectedSwatchBackground;
    }
  };

  let colorOptionName = '';
  let colorOptionValue = '';

  for (const [optionName, optionValue] of Object.entries(selectedOptions || {})) {
    const lowerName = optionName.toLowerCase();
    if (lowerName === 'color' || lowerName === 'colour') {
      colorOptionName = optionName;
      colorOptionValue = optionValue;
      break;
    }
  }

  if (!colorOptionName || !colorOptionValue) {
    applyBackground(productCard, '');
    applyBackground(productFormContainer, '');
    return;
  }

  const preview = findColorSwatchPreview(picker, colorOptionName, colorOptionValue);
  if (!preview) {
    applyBackground(productCard, '');
    applyBackground(productFormContainer, '');
    return;
  }

  const backgroundValue = extractSwatchBackground(preview);
  applyBackground(productCard, backgroundValue);
  applyBackground(productFormContainer, backgroundValue);
}

/**
 * @param {HTMLElement} picker
 * @param {HTMLElement} productCard
 * @param {HTMLInputElement | null} selectedOption
 * @returns {{ primary: string, secondary: string }}
 */
function getActiveVariantImageUrls(picker, productCard, selectedOption) {
  const urls = {
    primary: '',
    secondary: '',
  };

  if (selectedOption instanceof HTMLInputElement) {
    // Get image URLs from data attributes - try both getAttribute and dataset
    const primaryUrl = selectedOption.getAttribute('data-image-url') || selectedOption.dataset.imageUrl || '';
    const secondaryUrl = selectedOption.getAttribute('data-secondary-image-url') || selectedOption.dataset.secondaryImageUrl || '';
    
    if (primaryUrl) {
      urls.primary = primaryUrl;
    }
    if (secondaryUrl) {
      urls.secondary = secondaryUrl;
    }
  }

  if (!urls.primary || !urls.secondary) {
    const activeColorInput = picker.querySelector('input[type="radio"][data-image-url][checked]');
    if (activeColorInput instanceof HTMLInputElement) {
      if (!urls.primary && activeColorInput.dataset.imageUrl) {
        urls.primary = activeColorInput.dataset.imageUrl;
      }
      if (!urls.secondary && activeColorInput.dataset.secondaryImageUrl) {
        urls.secondary = activeColorInput.dataset.secondaryImageUrl;
      }
    }
  }

  if (!urls.primary || !urls.secondary) {
    const activeSwiperSwatch = productCard.querySelector('.color-swiper-container .color-swatch[data-active="true"]');
    if (activeSwiperSwatch instanceof HTMLElement) {
      if (!urls.primary && activeSwiperSwatch.dataset.imageUrl) {
        urls.primary = activeSwiperSwatch.dataset.imageUrl;
      }
      if (!urls.secondary && activeSwiperSwatch.dataset.secondaryImageUrl) {
        urls.secondary = activeSwiperSwatch.dataset.secondaryImageUrl;
      }
    }
  }

  const primaryImage = productCard.querySelector('.product-image-primary');
  if (primaryImage instanceof HTMLImageElement) {
    if (!primaryImage.dataset.originalSrc) {
      primaryImage.dataset.originalSrc = primaryImage.currentSrc || primaryImage.src;
    }
    if (!urls.primary) {
      urls.primary = primaryImage.dataset.originalSrc || primaryImage.src;
    }
  }

  const secondaryImage = productCard.querySelector('.product-image-secondary');
  if (secondaryImage instanceof HTMLImageElement) {
    if (!secondaryImage.dataset.originalSrc) {
      secondaryImage.dataset.originalSrc = secondaryImage.currentSrc || secondaryImage.src;
    }
    if (!urls.secondary) {
      urls.secondary = secondaryImage.dataset.originalSrc || secondaryImage.src;
    }
  }

  // If secondary is missing (or accidentally equals primary), derive it from product image ordering:
  // use the next DIFFERENT image after the active primary in `data-product-images`.
  const primaryKey = normalizeUrl(urls.primary);
  const secondaryKey = normalizeUrl(urls.secondary);
  if (primaryKey && (!secondaryKey || secondaryKey === primaryKey)) {
    const ordered = getProductImages(productCard);
    if (ordered) {
      const idx = ordered.findIndex((u) => normalizeUrl(u) === primaryKey);
      if (idx >= 0 && ordered.length > 1) {
        for (let i = idx + 1; i < ordered.length; i++) {
          const candidate = ordered[i];
          const candKey = normalizeUrl(candidate);
          if (!candKey || candKey === primaryKey) continue;

          const looksLikeAnotherPrimary =
            /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
          if (looksLikeAnotherPrimary) continue;

          urls.secondary = candidate;
          break;
        }
      }
    }
  }

  return urls;
}

/**
 * @param {HTMLElement} productCard
 * @param {string} primaryImageUrl
 * @param {string} secondaryImageUrl
 * @returns {void}
 */
function updateProductCardPrimaryImage(productCard, primaryImageUrl, secondaryImageUrl) {
  if (!(productCard instanceof HTMLElement)) return;

  // Ensure URLs have protocol (handle protocol-relative URLs starting with //)
  if (primaryImageUrl && primaryImageUrl.startsWith('//')) {
    primaryImageUrl = window.location.protocol + primaryImageUrl;
  }
  if (secondaryImageUrl && secondaryImageUrl.startsWith('//')) {
    secondaryImageUrl = window.location.protocol + secondaryImageUrl;
  }

  // Try multiple selectors to find primary image
  let primaryImage = productCard.querySelector('.product-image-primary');
  if (!primaryImage) {
    // Fallback: try to find the main product image
    const imageContainer = productCard.querySelector('.product-card__image');
    if (imageContainer) {
      primaryImage = imageContainer.querySelector('img:first-child, img');
    }
  }
  if (!primaryImage) {
    // Another fallback: find any image in the product card
    primaryImage = productCard.querySelector('.product-card__image img, img[class*="product"]');
  }

  const secondaryImage = productCard.querySelector('.product-image-secondary');

  // Update primary image immediately - no delays
  if (primaryImage instanceof HTMLImageElement && primaryImageUrl) {
    if (!primaryImage.dataset.originalSrc) {
      primaryImage.dataset.originalSrc = primaryImage.currentSrc || primaryImage.src;
    }
    // Replace width=300 with width=600 for better quality, or use original if no width param
    let finalPrimaryUrl = primaryImageUrl;
    if (finalPrimaryUrl.includes('width=300')) {
      finalPrimaryUrl = finalPrimaryUrl.replace('width=300', 'width=600');
    } else if (finalPrimaryUrl.includes('&width=')) {
      finalPrimaryUrl = finalPrimaryUrl.replace(/&width=\d+/, '&width=600');
    }
    
    // CRITICAL: Store the URL we're setting so we can verify it later
    primaryImage.dataset.lastSetImageUrl = finalPrimaryUrl;
    
    // Set src and srcset immediately for instant update
    // Set multiple times to ensure it sticks
    primaryImage.src = finalPrimaryUrl;
    primaryImage.src = finalPrimaryUrl; // Set twice to force update
    if (primaryImage.srcset !== undefined) {
      primaryImage.srcset = finalPrimaryUrl;
    }
    // Force browser to load the new image
    primaryImage.loading = 'eager';
    // Remove any error handlers that might prevent loading
    primaryImage.removeAttribute('data-error-handled');
    
    // Double-check after a brief delay to ensure it stuck
    setTimeout(() => {
      if (primaryImage.src !== finalPrimaryUrl && primaryImage.dataset.lastSetImageUrl === finalPrimaryUrl) {
        primaryImage.src = finalPrimaryUrl;
        if (primaryImage.srcset !== undefined) {
          primaryImage.srcset = finalPrimaryUrl;
        }
      }
    }, 10);
  }

  // Update secondary image immediately - no delays
  if (secondaryImage instanceof HTMLImageElement && secondaryImageUrl) {
    if (!secondaryImage.dataset.originalSrc) {
      secondaryImage.dataset.originalSrc = secondaryImage.currentSrc || secondaryImage.src;
    }
    // Replace width=300 with width=600 for better quality, or use original if no width param
    let finalSecondaryUrl = secondaryImageUrl;
    if (finalSecondaryUrl.includes('width=300')) {
      finalSecondaryUrl = finalSecondaryUrl.replace('width=300', 'width=600');
    } else if (finalSecondaryUrl.includes('&width=')) {
      finalSecondaryUrl = finalSecondaryUrl.replace(/&width=\d+/, '&width=600');
    }
    
    // CRITICAL: Store the URL we're setting so we can verify it later
    secondaryImage.dataset.lastSetImageUrl = finalSecondaryUrl;
    
    // Set src and srcset immediately for instant update
    // Set multiple times to ensure it sticks
    secondaryImage.src = finalSecondaryUrl;
    secondaryImage.src = finalSecondaryUrl; // Set twice to force update
    if (secondaryImage.srcset !== undefined) {
      secondaryImage.srcset = finalSecondaryUrl;
    }
    // Force browser to load the new image
    secondaryImage.loading = 'eager';
    // Remove any error handlers that might prevent loading
    secondaryImage.removeAttribute('data-error-handled');
    
    // Double-check after a brief delay to ensure it stuck
    setTimeout(() => {
      if (secondaryImage.src !== finalSecondaryUrl && secondaryImage.dataset.lastSetImageUrl === finalSecondaryUrl) {
        secondaryImage.src = finalSecondaryUrl;
        if (secondaryImage.srcset !== undefined) {
          secondaryImage.srcset = finalSecondaryUrl;
        }
      }
    }, 10);
  }
}

// Expose globally for use in other scripts (e.g., sunice-color-swiper.js)
globalWindow.updateProductCardPrimaryImage = updateProductCardPrimaryImage;

async function fetchCartJson() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to load cart details (status: ${response.status})`);
  }

  return response.json();
}

/**
 * @param {any} cart
 * @param {string} [sourceId]
 */
function dispatchCartUpdate(cart, sourceId = 'sunice-product-card') {
  const detail = {
    resource: cart,
    sourceId,
    data: {
      source: sourceId,
      itemCount: cart?.item_count ?? 0,
      sections: undefined
    }
  };

  const event = new CustomEvent('cart:update', {
    detail
  });

  document.dispatchEvent(event);
}

function openCartDrawer() {
  setTimeout(() => {
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const cartDrawerEl = /** @type {any} */ (cartDrawer);
      if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
        cartDrawerEl.open();
      } else {
        const cartButton = document.querySelector('.header-actions__action[on\\:click="/open"]');
        if (cartButton instanceof HTMLElement && typeof cartButton.click === 'function') {
          cartButton.click();
        }
      }
    } catch (error) {
      // Failed to open cart drawer
    }
  }, 250);
}

/**
 * @param {HTMLButtonElement | null} button
 * @param {'loading' | 'success' | 'error' | 'idle'} state
 */
function setAddToCartButtonState(button, state) {
  if (!(button instanceof HTMLButtonElement)) return;

  const defaultText = button.dataset.defaultText || button.textContent?.trim() || 'Add to Cart';
  if (!button.dataset.defaultText) {
    button.dataset.defaultText = defaultText;
  }

  if (state === 'loading') {
    button.disabled = true;
    button.classList.add('button--loading');
    button.setAttribute('aria-busy', 'true');
  } else {
    button.disabled = false;
    button.classList.remove('button--loading');
    button.removeAttribute('aria-busy');

    if (state === 'success') {
      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = button.dataset.defaultText || defaultText;
      }, 2000);
    } else if (state === 'error') {
      button.textContent = 'Try Again';
      setTimeout(() => {
        button.textContent = button.dataset.defaultText || defaultText;
      }, 2000);
    } else {
      button.textContent = button.dataset.defaultText || defaultText;
    }
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string | number} productId
 */
async function addProductCardToCart(form, productId) {
  const variantIdInputElement = form.querySelector('input[name="id"]');
  const variantIdInput = variantIdInputElement instanceof HTMLInputElement ? variantIdInputElement : null;
  const quantityInputElement = form.querySelector('input[name="quantity"]');
  const quantityInput = quantityInputElement instanceof HTMLInputElement ? quantityInputElement : null;
  const addButtonElement = form.querySelector('[name="add"]');
  const addButton = addButtonElement instanceof HTMLButtonElement ? addButtonElement : null;

  const variantId = variantIdInput?.value?.trim();
  if (!variantId) {
    alert('Please select all required options before adding to cart.');
    return;
  }

  const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

  setAddToCartButtonState(addButton, 'loading');

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity
          }
        ]
      })
    });

    const data = await response.json();

    if (!response.ok || data?.status) {
      const errorMessage = data?.description || data?.message || 'Unable to add this product to the cart.';
      throw new Error(errorMessage);
    }

    let cart = null;
    try {
      cart = await fetchCartJson();
    } catch (cartError) {
      // Added to cart but failed to refresh cart data
    }

    if (cart) {
      dispatchCartUpdate(cart, `sunice-product-card-${productId}`);
    }

    // If a mobile modal is open, close it after successful add-to-cart
    try {
      /** @type {any} */
      const globalWindow = window;
      if (typeof globalWindow.closeSuniceProductFormModal === 'function') {
        globalWindow.closeSuniceProductFormModal();
      }
    } catch (e) {
      // Failed to close product form modal after add-to-cart
    }

    openCartDrawer();
    setAddToCartButtonState(addButton, 'success');
  } catch (error) {
    alert(error.message || 'There was an issue adding this product to your cart. Please try again.');
    setAddToCartButtonState(addButton, 'error');
  } finally {
    if (addButton instanceof HTMLButtonElement && addButton.disabled && addButton.classList.contains('button--loading')) {
      setAddToCartButtonState(addButton, 'idle');
    }
  }
}

/**
 * @param {SubmitEvent} event
 * @param {string | number} productId
 */
globalWindow.handleShopifyAddToCart = function(event, productId) {
  runSuniceProductCardAddToCart(event, productId);
};
globalWindow.__suniceRunProductCardAddToCart = runSuniceProductCardAddToCart;
window.handleShopifyAddToCart = globalWindow.handleShopifyAddToCart;

// Initialize Core legend (Core: Color name) for a product card - used after pagination/dynamic load
function initializeCoreLegendForCard(productCard) {
  if (!(productCard instanceof HTMLElement)) return;
  const productId = productCard.getAttribute('data-product-id');
  const coreLegend = productCard.querySelector('.core-legend[data-product-id="' + productId + '"]');
  if (!coreLegend) return;
  let valueSpan = coreLegend.querySelector('.variant-option__selected-value');
  if (!valueSpan) {
    valueSpan = document.createElement('span');
    valueSpan.className = 'variant-option__selected-value';
    coreLegend.textContent = 'Core:';
    coreLegend.appendChild(valueSpan);
  }
  const checkedRadio = productCard.querySelector('.sunice-variant-picker .color-swatch input[type="radio"]:checked');
  if (checkedRadio instanceof HTMLInputElement && checkedRadio.value) {
    valueSpan.textContent = checkedRadio.value;
    return;
  }
  const firstSwatch = productCard.querySelector('.color-swiper-container .color-swatch[data-active="true"], .sunice-variant-picker .color-swatch[data-option-available="true"]');
  if (firstSwatch instanceof HTMLElement) {
    const colorName = firstSwatch.getAttribute('data-color') || firstSwatch.getAttribute('title') || '';
    if (colorName) valueSpan.textContent = colorName;
  }
}

// Initialize components when DOM is ready
function initializeSuniceProductCardComponents() {
  initSuniceProductCardAddToCartDelegation();

  // Setup variant pickers (only initializes those without data-initialized)
  setupSuniceVariantPicker();

  // Sync Core legend for all product cards (ensures paginated/dynamic cards show correct color)
  document.querySelectorAll('.sunice-product-card').forEach(function(card) {
    if (card instanceof HTMLElement) initializeCoreLegendForCard(card);
  });

  // Register with theme editor system for automatic re-initialization
  if (typeof globalWindow.registerThemeEditorComponent === 'function') {
    globalWindow.registerThemeEditorComponent(initializeSuniceProductCardComponents, 'sunice-product-cards');
  }

  // Add form validation to ensure complete variant selection
  if (!document.documentElement.hasAttribute('data-sunice-product-card-submit-listener')) {
    document.documentElement.setAttribute('data-sunice-product-card-submit-listener', 'true');
    document.addEventListener('submit', function(event) {
      if (!(event instanceof SubmitEvent)) return;
      if (event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof HTMLFormElement)) return;
      const form = target;
      if (!form.classList.contains('product-form')) return;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
      if (!form.closest('.sunice-product-card')) return;
      
      // Ensure variant ID is synced before submission (for modal forms)
      syncSuniceProductCardVariantId(form);

      const pickerElement = form.querySelector('.sunice-variant-picker');
      if (!(pickerElement instanceof HTMLElement)) return;

      if (form.classList.contains('product-form')) {
        const variantsDataScript = pickerElement.querySelector('.product-variants-data');

        if (variantsDataScript instanceof HTMLScriptElement) {
          let productData;
          try {
            productData = /** @type {any} */ (JSON.parse(variantsDataScript.textContent || '{}'));
          } catch (error) {
            return;
          }

          const variantsList = /** @type {Array<any>} */ (productData?.variants || []);
          const optionsList = /** @type {Array<any>} */ (productData?.options || []);

          const fieldsets = Array.from(pickerElement.querySelectorAll('fieldset'));

        // Check if all required options are selected
        let allOptionsSelected = true;
          fieldsets.forEach((fieldset) => {
            if (!(fieldset instanceof HTMLFieldSetElement)) return;
            const hasSelection = fieldset.querySelector('input[type="radio"]:checked');
          if (!hasSelection) {
            allOptionsSelected = false;
          }
        });

        if (!allOptionsSelected) {
          event.preventDefault();
          alert('Please select all required options before adding to cart.');
          return false;
        }

        // Validate that the selected combination exists and set the variant ID
          const selectedOptions = /** @type {Record<string, string>} */ ({});
          const radioButtons = Array.from(pickerElement.querySelectorAll('input[type="radio"]:checked'));

          const productId = pickerElement.dataset.productId || '';
          radioButtons.forEach((radio) => {
            if (!(radio instanceof HTMLInputElement)) return;
            const nameWithProductId = radio.name;
          const optionName = nameWithProductId.replace(`-${productId}`, '');
          const optionValue = radio.value;
          selectedOptions[optionName] = optionValue;
        });

        // Find the exact matching variant
          let matchingVariant = null;
          for (const variant of variantsList) {
          let matches = true;
          for (const optionName in selectedOptions) {
              const option = optionsList.find((opt) => opt?.name === optionName);
            if (!option) continue;

            const optionPosition = option.position - 1;
            const expectedValue = selectedOptions[optionName];
              const variantValue = variant.options?.[optionPosition];

            if (variantValue && expectedValue &&
                variantValue.toString().toLowerCase().trim() !== expectedValue.toString().toLowerCase().trim()) {
              matches = false;
              break;
            }
          }
          if (matches) {
            matchingVariant = variant;
            break;
          }
        }

        if (!matchingVariant) {
          event.preventDefault();
          alert('The selected combination is not available. Please choose different options.');
          return false;
        }

        // Set the variant ID in the hidden input
        const variantIdInput = form.querySelector('input[name="id"]');
          if (variantIdInput instanceof HTMLInputElement) {
            variantIdInput.value = String(matchingVariant.id);
          }
      }
    }
    });
  }
}

// Expose initializer globally so other scripts (like recommendations) can re-run it
globalWindow.initializeSuniceProductCardComponents = initializeSuniceProductCardComponents;

// CRITICAL: Unified click handler for ALL product card interactions (optimized for performance)
// Handles both variant picker options AND swiper swatches in a single event listener
document.addEventListener('click', function(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  
  // First check for swiper swatch clicks (most common interaction)
  const swatchButton = target.closest('.color-swiper-container .color-swatch');
  if (swatchButton instanceof HTMLElement) {
    const productCard = swatchButton.closest('.sunice-product-card');
    if (productCard instanceof HTMLElement) {
      // Update images
      const imageUrl = swatchButton.dataset.imageUrl || "";
      let secondaryImageUrl = swatchButton.dataset.secondaryImageUrl || "";
      
      // Find next image in product ordering
      const ordered = getProductImages(productCard);
      if (ordered) {
        const primaryKey = normalizeUrl(imageUrl);
        const idx = ordered.findIndex((u) => normalizeUrl(u) === primaryKey);
        if (idx >= 0 && ordered.length > 1) {
          for (let i = idx + 1; i < ordered.length; i++) {
            const candidate = ordered[i];
            const candKey = normalizeUrl(candidate);
            if (candKey && candKey !== primaryKey) {
              const looksLikeAnotherPrimary =
                /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
              if (looksLikeAnotherPrimary) continue;
              secondaryImageUrl = candidate || secondaryImageUrl;
              break;
            }
          }
        }
      }
      
      updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);

      // Update product card links with the selected variant so navigation goes to the correct variant
      const variantId = swatchButton.getAttribute('data-variant-id');
      if (variantId) {
        const firstLink = productCard.querySelector('a[href*="/products/"]');
        const baseUrl = firstLink?.href?.split('?')[0] || productCard.querySelector('.sunice-variant-picker')?.getAttribute('data-product-url') || '';
        if (baseUrl) {
          const url = new URL(baseUrl, window.location.origin);
          url.searchParams.set('variant', variantId);
          const newUrl = url.toString();
          productCard.querySelectorAll('a[href*="/products/"]').forEach((link) => {
            if (link instanceof HTMLAnchorElement) link.href = newUrl;
          });
          const cardGalleryLink = productCard.querySelector('.product-card__gallery-link');
          if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = newUrl;
        }
      }

      // Only the clicked swatch gets active; remove from all others (other colours must NOT get active)
      const swiperContainer = swatchButton.closest('.color-swiper-container');
      if (swiperContainer instanceof HTMLElement) {
        swiperContainer.querySelectorAll('.color-swatch').forEach((s) => {
          s.removeAttribute('data-active');
          s.classList.remove('active');
        });
        swatchButton.setAttribute('data-active', 'true');
      }
      
      // Update variant-title
      const variantTitle = productCard.querySelector('.variant-title');
      if (variantTitle instanceof HTMLElement) {
        const colorName = swatchButton.dataset.color || swatchButton.getAttribute('title') || '';
        if (colorName) {
          variantTitle.textContent = colorName;
        }
      }
      
      // Sync with radio button in variant picker
      const colorValue = swatchButton.dataset.color;
      if (colorValue) {
        const picker = productCard.querySelector('.sunice-variant-picker');
        if (picker instanceof HTMLElement) {
          const colorFieldsets = Array.from(picker.querySelectorAll('fieldset'));
          for (const fieldset of colorFieldsets) {
            if (!(fieldset instanceof HTMLFieldSetElement)) continue;
            const legend = fieldset.querySelector('legend');
            const optionName = legend?.textContent?.trim();
            if (optionName && (optionName.toLowerCase() === 'color' || optionName.toLowerCase() === 'colour')) {
              const radioButtons = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
              for (const radio of radioButtons) {
                if (!(radio instanceof HTMLInputElement)) continue;
                const normalizedRadioValue = radio.value.toLowerCase().trim();
                const normalizedColorValue = colorValue.toLowerCase().trim();
                if (normalizedRadioValue === normalizedColorValue) {
                  radioButtons.forEach((r) => {
                    if (r instanceof HTMLInputElement && r !== radio) {
                      r.checked = false;
                    }
                  });
                  radio.checked = true;
                  updateProductCardLegends(picker);
                  updateAvailabilityForColorSelection(picker, colorValue);
                  break;
                }
              }
              break;
            }
          }
        }
      }
      return; // Exit early - swiper swatch handled
    }
  }
  
  // Check if click is on any option (color swatch, size option, etc.) in variant picker
  const optionLabel = target.closest('.sunice-variant-picker .color-swatch, .sunice-variant-picker label[for*="option"], .sunice-variant-picker .variant-option');
  if (!(optionLabel instanceof HTMLElement)) return;
  
  // Find the radio button
  let radio = optionLabel.querySelector('input[type="radio"]');
  if (!(radio instanceof HTMLInputElement)) {
    // Try finding by the label's 'for' attribute
    const labelFor = optionLabel.getAttribute('for');
    if (labelFor) {
      radio = document.getElementById(labelFor);
    }
  }
  if (!(radio instanceof HTMLInputElement)) return;
  
  // Find the product card - try multiple methods
  const picker = optionLabel.closest('.sunice-variant-picker');
  if (!(picker instanceof HTMLElement)) return;
  
  let productCard = picker.closest('.sunice-product-card');
  if (!productCard) {
    const formContainer = picker.closest('.product-form-container');
    if (formContainer) {
      productCard = formContainer.closest('.sunice-product-card');
    }
  }
  if (!productCard) {
    const productId = picker.dataset.productId;
    if (productId) {
      productCard = document.querySelector('.sunice-product-card[data-product-id="' + productId + '"]');
    }
  }
  
  if (!(productCard instanceof HTMLElement)) return;
  
  // SIMPLE SOLUTION: For color swatches, find and click the corresponding swiper swatch
  // This reuses all the existing, working logic from the color swiper
  const productId = picker.dataset.productId || '';
  const optionName = radio.name.replace(`-${productId}`, '');
  const isColorOption = optionName === 'Color' || optionName === 'Colour';
  
  if (isColorOption) {
    // Find the corresponding swatch in the color swiper container
    const colorSwiperContainer = productCard.querySelector('.color-swiper-container');
    if (colorSwiperContainer instanceof HTMLElement) {
      // Try to find matching swatch by variant ID first (most reliable)
      const variantId = radio.getAttribute('data-variant-id') || radio.dataset.variantId || '';
      let matchingSwatch = null;
      
      if (variantId) {
        matchingSwatch = colorSwiperContainer.querySelector('.color-swatch[data-variant-id="' + variantId + '"]');
      }
      
      // If not found by variant ID, try to find by color value
      if (!matchingSwatch) {
        const colorValue = radio.value || '';
        const allSwatches = colorSwiperContainer.querySelectorAll('.color-swatch');
        for (const swatch of allSwatches) {
          if (swatch instanceof HTMLElement) {
            // Try multiple ways to get the color value from the swatch
            const swatchColor = swatch.dataset.color || 
                               swatch.getAttribute('data-color') || 
                               swatch.getAttribute('title') || 
                               swatch.textContent?.trim() || '';
            
            // Normalize both values for comparison
            const normalizedSwatchColor = swatchColor.toLowerCase().trim();
            const normalizedColorValue = colorValue.toLowerCase().trim();
            
            if (normalizedSwatchColor === normalizedColorValue || 
                normalizedSwatchColor.includes(normalizedColorValue) ||
                normalizedColorValue.includes(normalizedSwatchColor)) {
              matchingSwatch = swatch;
              break;
            }
          }
        }
      }
      
      // If we found a matching swatch, click it to trigger all the existing logic
      if (matchingSwatch instanceof HTMLElement) {
        // Use a small delay to ensure the radio is checked first
        setTimeout(() => {
          matchingSwatch.click();
        }, 10);
        return; // Exit early - the swiper click handler will handle everything
      }
    }
    
    // Fallback: If no matching swiper swatch found, update images directly using the same logic as swiper
    // This handles cases where the swiper might not exist or matching fails
    const imageUrl = radio.getAttribute('data-image-url') || radio.dataset.imageUrl || '';
    let secondaryImageUrl = radio.getAttribute('data-secondary-image-url') || radio.dataset.secondaryImageUrl || '';

    // Prefer "next image in product ordering" after the selected variant's primary image
    // This matches the swiper click handler logic exactly
    const ordered = getProductImages(productCard);
    if (ordered) {
      const primaryKey = normalizeUrl(imageUrl);
      const idx = ordered.findIndex((u) => normalizeUrl(u) === primaryKey);
        if (idx >= 0 && ordered.length > 1) {
          // Find the next DIFFERENT image after the primary (skip duplicates)
          for (let i = idx + 1; i < ordered.length; i++) {
            const candidate = ordered[i];
            const candKey = normalizeUrl(candidate);
            // Also avoid picking another variant's primary "a" image as hover.
            // We prefer the immediate "b" follow-up image if your naming follows *_a / *_b.
            if (candKey && candKey !== primaryKey) {
              const looksLikeAnotherPrimary =
                /_a(\.|$)/i.test(candKey) && !/_a(\.|$)/i.test(primaryKey);
              if (looksLikeAnotherPrimary) continue;
              secondaryImageUrl = candidate || secondaryImageUrl;
              break;
            }
          }
        }
      }
    
    // Update images using the same function as swiper click handler
    if (imageUrl || secondaryImageUrl) {
      updateProductCardPrimaryImage(productCard, imageUrl, secondaryImageUrl);
    }
    return; // Exit early - only color options update images
  }
  
  // For size or other options, DO NOT update images
  // Images should only change when color is selected
  // Return early without updating images
}, false); // Bubble phase - runs AFTER other handlers


// Auto-initialize when DOM is ready - use requestIdleCallback for non-critical initialization
const initProductCards = () => {
  initSuniceProductCardAddToCartDelegation();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Critical: initialize immediately
      initializeSuniceProductCardComponents();
    }, { once: true });
  } else {
    // DOM already ready - defer non-critical parts
    requestIdleCallback(() => {
      initializeSuniceProductCardComponents();
    }, { timeout: 2000 }); // Fallback after 2s if idle callback never fires
  }
};
initProductCards();

// Theme editor: only re-init on section:load (full section replace) and section:reorder
// block:select / block:deselect / section:select / section:deselect don't change product card DOM
var _productCardEditorTimeout = null;
function _debouncedProductCardEditorInit() {
  if (_productCardEditorTimeout) clearTimeout(_productCardEditorTimeout);
  _productCardEditorTimeout = setTimeout(function() {
    _productCardEditorTimeout = null;
    initializeSuniceProductCardComponents();
  }, 300);
}
document.addEventListener('shopify:section:load', _debouncedProductCardEditorInit);
document.addEventListener('shopify:section:reorder', _debouncedProductCardEditorInit);

// Re-initialize when new product cards are added (pagination, filters, section re-render)
(function() {
  var initTimeout = null;
  var initDebounceMs = 300;
  function scheduleInit() {
    if (initTimeout) clearTimeout(initTimeout);
    initTimeout = setTimeout(function() {
      initTimeout = null;
      if (document.querySelectorAll('.sunice-variant-picker:not([data-initialized])').length > 0) {
        initializeSuniceProductCardComponents();
      }
    }, initDebounceMs);
  }
  function hasNewProductCards(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!(n instanceof HTMLElement)) continue;
      if (n.classList && (n.classList.contains('sunice-product-card') || n.classList.contains('sunice-variant-picker'))) return true;
      if (n.querySelector && (n.querySelector('.sunice-product-card') || n.querySelector('.sunice-variant-picker'))) return true;
    }
    return false;
  }
  var observer = new MutationObserver(function(mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      if (added && added.length && hasNewProductCards(Array.from(added))) {
        scheduleInit();
        break;
      }
    }
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function() {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();

})(); // End IIFE guard

